/**
 * Stadtplan — Canvas 2D Renderer, Phase 1: Macro-Level Graph.
 * Zeichnet alle 12.164 Nodes + 35.074 Edges aus city_graph.json auf
 * absolutem Canvas anhand global_x / global_y Koordinaten.
 * Spec: corpllm-specs SPEC_ui_concept.md Kap. 3.7, 3.10
 */

import { resolveAsset } from '../data/AssetResolver';
import { fixture, mapData, SectorSnapshot, PlanEntryDTO } from '../data/fixture';
import {
  CityGraph, CityNode, Sector,
  CW, CH, TILE_SIZE, WORLD_W, WORLD_H, INIT_ZOOM, CELL_SIZE,
  TYPE_BG, toWorldX, toWorldY, initWorldDimensions,
} from '../map/types';
import { t } from '../i18n';
import { getLocalPlan } from './planning';
import {
  isPlanModeActive, togglePlanMode, openPlanMode, closePlanMode,
  mountRunnerBar, unmountRunnerBar,
  buildPlanContextItems, ACTION_VIS,
} from '../map/plan-ui';
import {
  FILTERS, isAnyFilterActive,
  recompute, getNodeFilter, getFilterColor, getFilterPlanAction,
  onFilterChange, setMatchSectors, type FilterId, type WorldBuildingLike,
} from '../map/filters';
import { createFilterSidebar, updateFilterSidebar } from '../map/filter-sidebar';

// ── Types (map-specific) ───────────────────────────────────────────────────

interface WorldBuilding {
  instance_id: string; node_id: string; sector_id: string;
  display_name: string; category: string;
  rent: number; buy_price: number; income_base: number;
  illegal: boolean; front_company: boolean;
  defense_value: number; zone: string; rentable: boolean;
  storage_cap: number; synergy_tags: string[];
  extort_rate: [number, number]; is_hq_slot: boolean;
  recruits_tag?: boolean;
  footprint?: [number, number]; footprint_cells?: string[];
}

type OverlayMode = 'type' | 'heat' | 'ownership' | 'prosperity';

// ── Constants ──────────────────────────────────────────────────────────────
// CW, CH, TILE_SIZE, GRID_SIZE, WORLD_W, WORLD_H, INIT_ZOOM, TYPE_BG
// are imported from '../map/types'

/** Canvas accent colour read from the live --ac token (CSS vars don't work
 *  in a 2D canvas ctx, so resolve the themeable accent in JS). */
const accent = (a: number): string =>
  `rgba(${getComputedStyle(document.documentElement).getPropertyValue('--ac').trim() || '0,245,212'}, ${a})`;

const TYPE_STROKE: Record<string, string> = {
  RESIDENTIAL_LOW:  '#2a3a5a', RESIDENTIAL_HIGH: '#3a4a6a',
  COMMERCIAL:       '#00b89e', INDUSTRIAL:       '#6a5a2a',
  WATERFRONT:       '#1a5a7a', GOVERNMENT:       '#6a2a4a',
  ENTERTAINMENT:    '#8a4a8a',
};

const OWNER_PALETTE = ['#e74c3c','#3498db','#9b59b6','#e67e22','#f1c40f','#2ecc71','#e91e8c'];
const ownerColorCache = new Map<string, string>();
let ownerColorIdx = 0;

// ── Road Auto-Tiling (4-way bitmask) ─────────────────────────────────────
// Direction bits: N=1, E=2, S=4, W=8.  Sum of occupied neighbors → tile variant.
const DIR_N = 1, DIR_E = 2, DIR_S = 4, DIR_W = 8;

// Road asphalt colors (AVENUE = major arterial, STREET = secondary)
const ROAD_FILL_AVENUE = '#1a2438';
const ROAD_FILL_STREET = '#151c2a';

// ── Top-Down Roof Textures ────────────────────────────────────────────────
// Source: Wall_XX_Y.png (1024×1024 top-down roof tiles, Synty CyberCity).
// Only textures that read as rooftops from above are used — front-facade
// textures (Wall_01, Wall_03, Wall_04, Wall_07, Wall_11–21) are excluded.
const ROOF_TEXTURES = [
  'Wall_02_A',   // 0: grey panel, cyan edge lights — corporate/office
  'Wall_05_A',   // 1: vents + hazard stripes + orange corners — industrial
  'Wall_06_A',   // 2: X-cross grey structural roof — residential/generic
  'Wall_08',     // 3: pipes + cyan glow + hazard — energy/infrastructure
  'Wall_09_A',   // 4: round dome with vents + cyan lights — tech/lab
  'Wall_10',     // 5: pipes + panels + red elements — heavy industry
];

// ── Category → roof texture index (max 2 per category for reduced variance) ─
// Multiple game categories map to the same visual roof class.
const CATEGORY_TILES: Record<string, number[]> = {
  'Gastronomie':     [0],         // corporate panel
  'Gesundheit':      [0],         // clean corporate
  'Produktion':      [1, 5],      // industrial
  'Wohnraum':        [2],         // structural X-cross
  'Unterkunft':      [2],         // structural X-cross
  'Transport':       [3],         // infrastructure pipes
  'Energie/Müll':    [3, 5],      // infrastructure + heavy industry
  'Dienstleistung':  [0],         // corporate panel
  'Sport/Wettkampf': [2, 0],      // structural + corporate
  'Logistik':        [1, 3],      // industrial + infrastructure
  'Bio & Chemie':    [4],         // tech dome
  'Lebensmittel':    [2],         // structural
  'Nachtleben':      [0, 4],      // corporate + tech
  'Infrastruktur':   [3, 1],      // infrastructure + industrial
  'Tech & Daten':    [4, 0],      // tech dome + corporate
  'Freizeit/Spiel':  [2, 0],      // structural + corporate
  'Retail (Tech)':   [4],         // tech dome
  'Fahrzeuge':       [1, 3],      // industrial + infrastructure
  'Retail (Alltag)': [2],         // structural
  'Cyberware':       [4, 0],      // tech dome + corporate
  'Schatten':        [5, 1],      // heavy industry + industrial
};

// Fallback for unknown categories
const DEFAULT_TILES = [2];

// ── Deterministic hash from instance_id (djb2) ───────────────────────────
function hashStr(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return h >>> 0;  // unsigned 32-bit
}

// Cyberpunk palette: dark muted bases + selective neon accents
const CATEGORY_TINT: Record<string, string> = {
  'Transport':       '#3a7acc',
  'Gastronomie':     '#cc7a3a',
  'Energie/Müll':    '#7a7a6a',
  'Gesundheit':      '#3a9a8a',
  'Dienstleistung':  '#5a6a8a',
  'Sport/Wettkampf': '#cc3a5a',
  'Logistik':        '#9a7a3a',
  'Produktion':      '#6a6a7a',
  'Bio & Chemie':    '#3a9a5a',
  'Lebensmittel':    '#5a9a5a',
  'Nachtleben':      '#9a3acc',  // neon accent category
  'Infrastruktur':   '#4a5a6a',
  'Tech & Daten':    '#2a8aaa',
  'Freizeit/Spiel':  '#aa3a7a',
  'Retail (Tech)':   '#3a5aaa',
  'Unterkunft':      '#8a7a50',
  'Fahrzeuge':       '#5a6a6a',
  'Retail (Alltag)': '#5a5a8a',
  'Cyberware':       '#2aaaaa',  // neon accent category
  'Wohnraum':        '#4a8a6a',
  'Schatten':        '#3a4a4a',
};

// Categories that get a subtle neon glow at high zoom
const NEON_GLOW: Record<string, string> = {
  'Nachtleben':      '#d946ef',
  'Cyberware':       '#00f5d4',
  'Gastronomie':     '#f97316',
  'Freizeit/Spiel':  '#f472b6',
  'Tech & Daten':    '#22d3ee',
};

function getOwnerColor(owner: string, mine: string): string {
  if (owner === mine) return accent(1);
  if (!ownerColorCache.has(owner)) ownerColorCache.set(owner, OWNER_PALETTE[ownerColorIdx++ % OWNER_PALETTE.length]);
  return ownerColorCache.get(owner)!;
}

function heatColor(h: number): string {
  if (h < 30) return '#2ecc71';
  if (h < 60) return '#f1c40f';
  if (h < 80) return '#e67e22';
  return '#e74c3c';
}

function getCategoryTint(cat: string): string {
  return CATEGORY_TINT[cat] ?? '#4a5a6a';
}

// ── renderMap ──────────────────────────────────────────────────────────────

export function renderMap(): HTMLElement {
  const root = document.createElement('div');
  root.className = 'screen';
  root.style.cssText = 'display:flex;flex-direction:column;height:100%;overflow:hidden;position:relative';

  const myCorpName = fixture.player?.corpName ?? '';
  const fixtureSectors = new Map<string, { heat: number; owner?: string; scouted: boolean }>(
    (fixture.sectors ?? []).map((t: SectorSnapshot) =>
      [t.sectorId, { heat: t.heat, owner: t.owner ?? undefined, scouted: t.scouted !== false }])
  );
  /** Set of scouted sector IDs — unscouted sectors get fog overlay */
  const scoutedSet = new Set(
    (fixture.sectors ?? []).filter((s: SectorSnapshot) => s.scouted !== false).map((s: SectorSnapshot) => s.sectorId)
  );
  const isScouted = (sid: string) => scoutedSet.size === 0 || scoutedSet.has(sid);

  // ── Styles ────────────────────────────────────────────────────────────────
  const style = document.createElement('style');
  style.textContent = `
    .map-toolbar { display:flex;align-items:center;gap:8px;padding:6px 10px;
      background:rgba(10,12,20,0.95);border-bottom:1px solid rgba(var(--ac),0.1);
      flex-wrap:wrap;flex-shrink:0 }
    .map-toolbar label { font-size:12px;color:#4a5a6a;font-family:monospace;margin-right:2px }
    /* .overlay-btn/.toggle-btn → shared .btn.sm (+ .active); markers kept for JS toggle */
    .map-wrap { position:relative;flex:1;overflow:hidden;background:#0a0c14 }
    #cityMap { display:block;width:100%;height:100%;cursor:grab;image-rendering:pixelated;touch-action:none }
    #cityMap.panning { cursor:grabbing }
    .map-legend { position:absolute;bottom:8px;left:50%;transform:translateX(-50%);
      background:rgba(10,12,20,0.8);padding:5px 14px;border-radius:3px;
      font-size:0.85rem;font-family:monospace;color:#4a5a6a;white-space:nowrap }
    .map-legend-bar { display:flex;align-items:center;gap:10px;flex-wrap:wrap;
      font-size:0.85rem;font-family:monospace;color:#7a8a9a }
    .ml-item { display:flex;align-items:center;gap:3px;white-space:nowrap }
    .ml-swatch { width:9px;height:9px;flex-shrink:0 }
    .ml-sep { width:1px;height:12px;background:#2a3a4a;flex-shrink:0 }
    #detailPanel { width:220px;flex-shrink:0;background:rgba(10,12,20,0.95);
      border-left:1px solid rgba(var(--ac),0.1);overflow-y:auto;padding:12px 10px;
      font-size:12px;font-family:monospace }
    .dp-id { font-size:12px;color:#4a5a6a;margin-bottom:2px }
    .dp-title { font-size:16px;font-weight:700;color:#e0e8f0;margin-bottom:2px;
      font-family:"Space Grotesk",system-ui,sans-serif }
    .dp-type { font-size:12px;color:#4a5a6a;text-transform:uppercase;margin-bottom:8px }
    .dp-section { font-size:11px;color:#3a4a5a;text-transform:uppercase;letter-spacing:.08em;
      margin:10px 0 4px;padding-bottom:3px;border-bottom:1px solid rgba(var(--ac),0.07) }
    .dp-row { display:flex;justify-content:space-between;padding:2px 0;color:#6a7a8a }
    .dp-label { color:#4a5a6a }
    .dp-val { font-family:monospace;color:#8a9aaa }
    /* .adj-tag → shared .chip (marker for JS [data-goto]); only the inline-flow margin stays */
    .adj-tag { margin:2px 3px 2px 0; cursor:pointer; }
    .bld-card { padding:5px 0;border-bottom:1px solid rgba(var(--ac),0.05) }
    .bld-name { font-size:11px;color:#8a9aaa }
    .bld-meta { font-size:12px;color:#4a5a6a;margin-top:1px }
    .map-area { display:flex;flex:1;overflow:hidden;min-height:0 }
    @media (max-width:1024px) {
      #detailPanel { width:180px }
    }
  `;
  root.appendChild(style);

  // ── Toolbar ───────────────────────────────────────────────────────────────
  const toolbar = document.createElement('div');
  toolbar.className = 'map-toolbar';
  toolbar.innerHTML = `
    <label>OVERLAY:</label>
    <button class="overlay-btn btn sm active" data-overlay="type">TYP</button>
    <button class="overlay-btn btn sm" data-overlay="heat">HEAT</button>
    <button class="overlay-btn btn sm" data-overlay="ownership">OWNERSHIP</button>
    <button class="overlay-btn btn sm" data-overlay="prosperity">WOHLSTAND</button>
    <button class="toggle-btn btn sm" id="heat-predict-toggle">HEAT VORSCHAU</button>
    <button class="toggle-btn btn sm" id="filter-toggle">FILTER</button>
    <button class="toggle-btn btn sm" id="plan-toggle" style="background:rgba(var(--ac),0.12);border-color:rgba(var(--ac),0.4);color:var(--cyan);font-weight:bold">▶ ${t('map.btn_plan')}</button>
    <div style="flex:1"></div>
    <span id="map-version-badge" style="font-size:11px;font-family:monospace;color:#4a5a6a"></span>
  `;
  root.appendChild(toolbar);

  // ── Main area ─────────────────────────────────────────────────────────────
  const mapArea = document.createElement('div');
  mapArea.className = 'map-area';
  root.appendChild(mapArea);

  // Canvas wrap
  const wrap = document.createElement('div');
  wrap.className = 'map-wrap';
  mapArea.appendChild(wrap);

  const canvas = document.createElement('canvas') as HTMLCanvasElement;
  canvas.id = 'cityMap';
  canvas.width  = CW;
  canvas.height = CH;
  wrap.appendChild(canvas);

  const legend = document.createElement('div');
  legend.className = 'map-legend';
  wrap.appendChild(legend);

  // ── Dynamic legend builder ─────────────────────────────────────────────
  const SECTOR_TYPE_LABELS: Record<string, string> = {
    RESIDENTIAL_LOW: 'Wohnen (niedrig)', RESIDENTIAL_HIGH: 'Wohnen (hoch)',
    COMMERCIAL: 'Kommerziell', INDUSTRIAL: 'Industriell',
    WATERFRONT: 'Hafen', GOVERNMENT: 'Regierung', ENTERTAINMENT: 'Unterhaltung',
  };

  const CATEGORY_LABELS: Record<string, string> = {
    'Transport': 'Transport', 'Gastronomie': 'Gastro', 'Energie/Müll': 'Energie',
    'Gesundheit': 'Gesundheit', 'Dienstleistung': 'Service', 'Sport/Wettkampf': 'Sport',
    'Logistik': 'Logistik', 'Produktion': 'Produktion', 'Bio & Chemie': 'Bio/Chemie',
    'Lebensmittel': 'Food', 'Nachtleben': 'Nachtleben', 'Infrastruktur': 'Infra',
    'Tech & Daten': 'Tech', 'Freizeit/Spiel': 'Freizeit', 'Retail (Tech)': 'R-Tech',
    'Unterkunft': 'Unterkunft', 'Fahrzeuge': 'Fahrzeuge', 'Retail (Alltag)': 'R-Alltag',
    'Cyberware': 'Cyberware', 'Wohnraum': 'Wohnraum', 'Schatten': 'Schatten',
  };

  let lastLegendZoomed = false;
  function updateLegend(overlay: OverlayMode, buildingZoom = false): void {
    const items: string[] = [];
    const swatch = (color: string, label: string, shape = 'border-radius:1px') =>
      `<span class="ml-item"><span class="ml-swatch" style="background:${color};${shape}"></span>${label}</span>`;

    if (buildingZoom) {
      // Building zoom: show category tints
      for (const [cat, label] of Object.entries(CATEGORY_LABELS)) {
        items.push(swatch(CATEGORY_TINT[cat] ?? '#4a5a6a', label));
      }
    } else {
      // Sector zoom: show overlay-specific legend
      switch (overlay) {
        case 'type':
          for (const [key, label] of Object.entries(SECTOR_TYPE_LABELS)) {
            items.push(swatch(TYPE_STROKE[key] ?? '#3a4a5a', label));
          }
          break;
        case 'heat':
          items.push(swatch('#2ecc71', '< 30'));
          items.push(swatch('#f1c40f', '30–59'));
          items.push(swatch('#e67e22', '60–79'));
          items.push(swatch('#e74c3c', '≥ 80'));
          break;
        case 'ownership':
          items.push(swatch('var(--cyan)', 'Eigenes Gebiet'));
          items.push(swatch('#9b59b6', 'KI-Zone'));
          items.push(swatch('#e74c3c', 'Gegner'));
          break;
        case 'prosperity':
          items.push(swatch('#fca31133', 'Niedrig'));
          items.push(swatch('#fca31177', 'Mittel'));
          items.push(swatch('#fca311cc', 'Hoch'));
          break;
      }
    }

    // Separator + permanent markers
    const accent = getAccentColor();
    items.push('<span class="ml-sep"></span>');
    items.push(`<span class="ml-item"><span style="color:${accent};font-size:11px;line-height:1">★</span> HQ</span>`);
    items.push(`<span class="ml-item"><span class="ml-swatch" style="border:1.5px solid ${accent};background:transparent"></span> ${t('map.own_buildings')}</span>`);

    legend.innerHTML = `<span class="map-legend-bar">${items.join('')}</span>`;
    lastLegendZoomed = buildingZoom;
  }
  updateLegend('type');

  const ctx = canvas.getContext('2d')!;

  // Detail panel
  const detailPanel = document.createElement('div');
  detailPanel.id = 'detailPanel';
  detailPanel.innerHTML = `<div style="color:#3a4a5a;font-size:12px;margin-top:20px;text-align:center">${t('map.select_sector')}</div>`;
  mapArea.appendChild(detailPanel);

  // ── Filter sidebar ────────────────────────────────────────────────────────
  let filterSidebarEl: HTMLElement | null = null;
  let filterSidebarOpen = false;

  function recomputeFilters(): void {
    if (!worldBldgs) return;
    const playerBldgs = fixture.player?.buildings ?? [];
    const ownedNodeIds = new Set(playerBldgs.map((b: { nodeId: string }) => b.nodeId));
    const asLike: Iterable<WorldBuildingLike> = [...worldBldgs.values()].map(b => ({
      node_id: b.node_id, buy_price: b.buy_price, income_base: b.income_base,
      illegal: b.illegal, rentable: b.rentable, extort_rate: b.extort_rate,
      is_hq_slot: b.is_hq_slot, storage_cap: b.storage_cap,
      recruits_tag: b.recruits_tag,
    }));
    recompute(asLike, ownedNodeIds);
    // Count matching sectors
    const matchSectors = new Set<string>();
    for (const b of worldBldgs.values()) {
      const fid = getNodeFilter(b.node_id);
      if (fid) matchSectors.add(b.sector_id);
    }
    setMatchSectors(matchSectors.size);
  }

  function toggleFilterSidebar(): void {
    if (filterSidebarOpen && filterSidebarEl) {
      filterSidebarEl.remove();
      filterSidebarEl = null;
      filterSidebarOpen = false;
      const btn = root.querySelector('#filter-toggle');
      if (btn) btn.classList.remove('active');
    } else {
      filterSidebarEl = createFilterSidebar({
        onToggle: () => { recomputeFilters(); if (filterSidebarEl) updateFilterSidebar(filterSidebarEl, filterSidebarCallbacks); draw(); },
        onClose: () => toggleFilterSidebar(),
      });
      mapArea.insertBefore(filterSidebarEl, mapArea.firstChild);
      filterSidebarOpen = true;
      const btn = root.querySelector('#filter-toggle');
      if (btn) btn.classList.add('active');
      recomputeFilters();
    }
  }

  const filterSidebarCallbacks = {
    onToggle: () => { recomputeFilters(); if (filterSidebarEl) updateFilterSidebar(filterSidebarEl, filterSidebarCallbacks); draw(); },
    onClose: () => toggleFilterSidebar(),
  };

  onFilterChange(() => draw());

  // ── State ─────────────────────────────────────────────────────────────────
  let graph: CityGraph | null = null;
  let nodeMap = new Map<string, CityNode>();
  let sectorMap = new Map<string, Sector>();
  let aiSectors = new Set<string>();
  let aiSectorName = new Map<string, string>();
  let selectedId: string | null = null;
  let currentOverlay: OverlayMode = 'type';
  let showHeatPrediction = false;
  let sectorHeatDeltas = new Map<string, number>();
  let showNodes = true;
  let worldBldgs: Map<string, WorldBuilding> | null = null;
  let sectorBounds = new Map<string, { x: number; y: number; w: number; h: number }>();
  /** BUILDING_FOOTPRINT node_id → entry BUILDING_ENTRY node_id */
  let footprintToEntry = new Map<string, string>();

  // Roof texture cache: index into ROOF_TEXTURES → loaded HTMLImageElement
  const roofImages = new Map<number, HTMLImageElement>();
  let spritesReady = false;

  // Road bitmask lookup (computed once after city_graph loads)
  let roadBitmasks = new Map<string, number>(); // node_id → bitmask (0–15)

  // Cross-sector road connectors — midpoints between gateway AVENUE nodes
  type RoadConnector = { wx: number; wy: number; horizontal: boolean };
  let crossConnectors: RoadConnector[] = [];

  // Y-sorted building list for painter's algorithm (built once after data loads)
  // tileIdx = index into ROOF_TEXTURES for the specific roof variant
  type SortedBuilding = { wx: number; wy: number; cat: string; tint: string; sectorCol: number; sectorRow: number; tileIdx: number; nodeId: string; sectorId: string; facing: string; blockId: number; isFootprint?: boolean; entryNodeId?: string; };
  let sortedBuildings: SortedBuilding[] | null = null;

  // A CityBlock is a contiguous group of BUILDING_ENTRY + ALLEY cells bounded by STREET/AVENUE.
  type CityBlock = {
    id: number;
    sectorId: string;
    sectorCol: number;
    sectorRow: number;
    // Bounding box in world coordinates
    minX: number; minY: number; maxX: number; maxY: number;
    // Which grid cells belong to this block (for interior rendering)
    cells: { row: number; col: number; type: string; wx: number; wy: number }[];
    // Which sides face a road (for facade rendering)
    facadeN: boolean[]; facadeE: boolean[]; facadeS: boolean[]; facadeW: boolean[];
    // Building nodes in this block
    buildings: string[];
    // Average tint for the block
    tint: string;
  };
  let cityBlocks: CityBlock[] | null = null;

  // Read player accent color from CSS variable (set via settings/lobby)
  function getAccentColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--cyan').trim() || '#00f5d4';
  }

  // Camera state — start zoomed out to show full map
  const camera = { x: 0, y: 0, zoom: INIT_ZOOM };
  let isPanning = false, lastMX = 0, lastMY = 0;

  // ── Lasso / Building Selection state ────────────────────────────────────
  const selectedBldgs = new Set<string>();  // node_ids of selected buildings
  let focusedBldgId: string | null = null;  // single-clicked building for detail panel
  let isLassoing = false;
  let lassoStartWorld: { x: number; y: number } | null = null;
  let lassoEndWorld: { x: number; y: number } | null = null;
  let lassoFab: HTMLElement | null = null;   // Floating Action Button

  // ── Coordinate helpers ────────────────────────────────────────────────────
  // toWorldX/toWorldY imported from '../map/types'
  const wx = toWorldX;
  const wy = toWorldY;

  // Canvas pixel → world coord
  function toWorld(cx: number, cy: number) {
    const r = canvas.getBoundingClientRect();
    const px = (cx / r.width)  * CW;
    const py = (cy / r.height) * CH;
    return {
      x: (px - camera.x) / camera.zoom,
      y: (py - camera.y) / camera.zoom,
    };
  }

  function sectorAt(worldX: number, worldY: number): Sector | null {
    if (!graph) return null;
    const { grid_cols, grid_rows } = graph.meta;
    const cellW = WORLD_W / grid_cols;
    const cellH = WORLD_H / grid_rows;
    const col = Math.floor(worldX / cellW);
    const row = Math.floor(worldY / cellH);
    if (col < 0 || col >= grid_cols || row < 0 || row >= grid_rows) return null;
    return graph.sectors.find(s => s.grid_col === col && s.grid_row === row) ?? null;
  }

  // Hit-test: find building at world coordinate
  const bldgHalfS = CELL_SIZE / 2;
  function buildingAt(worldX: number, worldY: number): SortedBuilding | null {
    if (!sortedBuildings) return null;
    // Tile size on screen in px — only hit-test when buildings are visible
    const tilePx = CELL_SIZE * camera.zoom;
    if (tilePx < 12) return null;
    for (let i = sortedBuildings.length - 1; i >= 0; i--) {
      const b = sortedBuildings[i];
      if (Math.abs(worldX - b.wx) < bldgHalfS && Math.abs(worldY - b.wy) < bldgHalfS) {
        // Footprint cell → resolve to entry building
        if (b.isFootprint && b.entryNodeId) {
          const entry = sortedBuildings.find(sb => sb.nodeId === b.entryNodeId && !sb.isFootprint);
          return entry ?? b;
        }
        return b;
      }
    }
    return null;
  }

  // ── Lasso helpers ──────────────────────────────────────────────────────────

  /** Check if a building is selectable for ROUTE (income > 0, not illegal, not player-owned). */
  function isBuildingSelectable(nodeId: string): boolean {
    const bldg = worldBldgs?.get(nodeId);
    if (!bldg) return false;
    if (bldg.income_base <= 0) return false;
    if (bldg.illegal) return false;
    // Check if player owns this building
    const playerBuildings = fixture.player?.buildings ?? [];
    if (playerBuildings.some((b: { nodeId?: string }) => b.nodeId === nodeId)) return false;
    return true;
  }

  /** Select all valid buildings inside a world-space rectangle (single sector). */
  function selectBuildingsInRect(x1: number, y1: number, x2: number, y2: number): void {
    if (!sortedBuildings) return;
    const minX = Math.min(x1, x2), maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2), maxY = Math.max(y1, y2);

    // Collect candidates and count per sector for single-sector constraint.
    const candidates: SortedBuilding[] = [];
    const sectorCounts = new Map<string, number>();
    for (const b of sortedBuildings) {
      if (b.wx >= minX && b.wx <= maxX && b.wy >= minY && b.wy <= maxY) {
        if (isBuildingSelectable(b.nodeId)) {
          candidates.push(b);
          sectorCounts.set(b.sectorId, (sectorCounts.get(b.sectorId) ?? 0) + 1);
        }
      }
    }

    // Find dominant sector (most buildings in rect).
    let dominantSector = '';
    let maxCount = 0;
    for (const [sid, count] of sectorCounts) {
      if (count > maxCount) { dominantSector = sid; maxCount = count; }
    }

    // Add only buildings from dominant sector.
    selectedBldgs.clear();
    for (const b of candidates) {
      if (b.sectorId === dominantSector) selectedBldgs.add(b.nodeId);
    }
  }

  /** Clear building selection and remove floating button. */
  function clearSelection(): void {
    selectedBldgs.clear();
    removeFab();
    draw();
  }

  /** Show/update the floating action button near the selection. */
  function showFab(): void {
    if (selectedBldgs.size === 0) { removeFab(); return; }
    if (!lassoFab) {
      lassoFab = document.createElement('div');
      lassoFab.style.cssText = `
        position:fixed; z-index:1050; cursor:pointer;
        background:rgba(var(--ac),0.15); border:1px solid rgba(var(--ac),0.6);
        padding:6px 14px; font-family:var(--mono); font-size:12px;
        color:var(--cyan); box-shadow:0 2px 8px rgba(0,0,0,0.5);
        transition:background 0.15s;
      `;
      lassoFab.addEventListener('mouseenter', () => { if (lassoFab) lassoFab.style.background = 'rgba(var(--ac),0.3)'; });
      lassoFab.addEventListener('mouseleave', () => { if (lassoFab) lassoFab.style.background = 'rgba(var(--ac),0.15)'; });
      lassoFab.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!isPlanModeActive()) openPlanMode(root);
        draw();
        // Show action menu for selected buildings at FAB position
        if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
        const nodes = [...selectedBldgs];
        if (nodes.length === 0) return;
        const sectorId = sortedBuildings?.find(b => b.nodeId === nodes[0])?.sectorId ?? '';
        const allActions = fixture.player.actions ?? [];
        const planItems = buildPlanContextItems(sectorId, allActions, {
          buildingNodeId: nodes[0],
          isOwned: fixture.player.buildings.some(b => b.nodeId === nodes[0] && b.owned),
        });
        if (planItems.length === 0) return;
        contextMenuEl = document.createElement('div');
        const fabRect = lassoFab!.getBoundingClientRect();
        contextMenuEl.style.cssText = `
          position:fixed; left:${fabRect.left}px; top:${fabRect.top - 8}px; transform:translateY(-100%);
          background:rgba(10,12,20,0.95); border:1px solid rgba(var(--ac),0.4);
          padding:4px; font-family:monospace; font-size:11px; z-index:1060;
          box-shadow:2px 2px 8px rgba(0,0,0,0.5);
        `;
        for (const item of planItems) {
          item.addEventListener('click', () => {
            if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
            draw();
          });
          contextMenuEl.appendChild(item);
        }
        document.body.appendChild(contextMenuEl);
      });
      document.body.appendChild(lassoFab);
    }
    lassoFab.textContent = t('planui.add_to_plan');

    // Position near the center of selection on screen.
    const r = canvas.getBoundingClientRect();
    let sumX = 0, sumY = 0, n = 0;
    for (const nodeId of selectedBldgs) {
      const b = sortedBuildings?.find(sb => sb.nodeId === nodeId);
      if (b) {
        const sx = (b.wx * camera.zoom + camera.x) / CW * r.width + r.left;
        const sy = (b.wy * camera.zoom + camera.y) / CH * r.height + r.top;
        sumX += sx; sumY += sy; n++;
      }
    }
    if (n > 0) {
      lassoFab.style.left = `${sumX / n - 80}px`;
      lassoFab.style.top = `${sumY / n + 30}px`;
    }
  }

  function removeFab(): void {
    if (lassoFab) { lassoFab.remove(); lassoFab = null; }
  }


  // ── Building Tooltip ───────────────────────────────────────────────────────
  let tooltipEl: HTMLElement | null = null;
  let tooltipTimer: ReturnType<typeof setTimeout> | null = null;
  let lastHoveredNodeId: string | null = null;

  function showBuildingTooltip(b: SortedBuilding, clientX: number, clientY: number): void {
    const bldg = worldBldgs?.get(b.nodeId);
    if (!bldg) return;

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.style.cssText = `
        position:fixed; z-index:1100; pointer-events:none;
        background:rgba(10,12,20,0.95); border:1px solid rgba(var(--ac),0.4);
        padding:8px 12px; font-family:var(--mono); font-size:11px;
        box-shadow:2px 2px 8px rgba(0,0,0,0.5); max-width:280px;
        line-height:1.5;
      `;
      document.body.appendChild(tooltipEl);
    }

    const fmtCash = (n: number): string => {
      if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000)    return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
      return `$${Math.round(n)}`;
    };

    // Determine zone from the owned player buildings if the player knows this one
    const observedBldg = (fixture.player.buildings ?? []).find(pb => pb.nodeId === b.nodeId);
    const zone = observedBldg?.zone ?? '';
    const defense = (bldg as unknown as { defense_value?: number }).defense_value ?? 0;
    const storage = (bldg as unknown as { storage_cap?: number }).storage_cap ?? 0;
    const rent    = (bldg as unknown as { rent?: number }).rent ?? 0;
    const buyPrice = (bldg as unknown as { buy_price?: number }).buy_price ?? 0;
    const synergy = (bldg as unknown as { synergy_tags?: string[] | null }).synergy_tags ?? [];

    const rows: string[] = [];
    if (bldg.income_base > 0) {
      rows.push(`<div>Income: <span style="color:var(--green)">${fmtCash(bldg.income_base)}</span>/Zyklus</div>`);
    }
    if (rent > 0) {
      rows.push(`<div>Miete: <span style="color:var(--amber)">${fmtCash(rent)}</span>/Zyklus</div>`);
    }
    if (buyPrice > 0) {
      rows.push(`<div>Kauf: <span style="color:var(--text-mid)">${fmtCash(buyPrice)}</span></div>`);
    }
    // v1.11.0: EXTORT/COLLECT now anchor on rent, not income_base × extort_rate.
    // Show concrete expected yields so the player can compare targets.
    if (rent > 0) {
      const extortYield = Math.round(rent * 0.25);
      const collectYield = Math.round(rent * 0.20);
      // Duration formula mirrors gamestate.ExtortionDurationDays
      const duration = Math.min(14, Math.max(3, 3 + Math.floor(rent / 1000)));
      rows.push(
        `<div style="color:var(--text-mid)">EXTORT: <span style="color:var(--amber)">${fmtCash(extortYield)}</span> · COLLECT/d: <span style="color:var(--amber)">${fmtCash(collectYield)}</span> · <span style="color:var(--text-lo)">${duration}d</span></div>`,
      );
    }
    // Active extortion on this target? Mark it.
    const myExtortion = (fixture.player.extortedBuildings ?? []).find(e => e.nodeId === b.nodeId);
    if (myExtortion) {
      const daysLeft = myExtortion.expiresAt - (fixture.player.cycle ?? 0);
      rows.push(
        `<div style="color:var(--amber);border-top:1px dashed rgba(252,163,17,0.3);padding-top:3px;margin-top:3px">⚡ Du erpresst dieses Gebäude · ${daysLeft}d verbleibend</div>`,
      );
    }
    if (defense > 0 || storage > 0) {
      const defStorageBits: string[] = [];
      if (defense > 0) defStorageBits.push(`DEF ${defense}`);
      if (storage > 0) defStorageBits.push(`Lager ${storage.toLocaleString('de-DE')}`);
      rows.push(`<div style="color:var(--text-mid);font-size:10px;margin-top:2px">${defStorageBits.join(' · ')}</div>`);
    }
    if (synergy && synergy.length > 0) {
      rows.push(
        `<div style="color:var(--text-lo);font-size:10px;margin-top:2px">Synergie: ${synergy.join(', ')}</div>`,
      );
    }

    const tags: string[] = [];
    if (observedBldg?.owned) tags.push('<span style="color:var(--cyan)">OWN</span>');
    if (bldg.front_company) tags.push('<span style="color:var(--amber)">Tarnfirma</span>');
    if (bldg.illegal)       tags.push('<span style="color:var(--red)">Illegal</span>');

    const subhead = [bldg.category, b.sectorId, zone].filter(Boolean).join(' · ');

    tooltipEl.innerHTML = `
      <div style="color:var(--cyan);font-weight:600;margin-bottom:2px">${bldg.display_name}</div>
      <div style="color:var(--text-lo);font-size:10px;letter-spacing:0.04em">${subhead}</div>
      <div style="color:var(--text-mid);margin-top:4px">${rows.join('')}</div>
      ${tags.length ? `<div style="margin-top:4px;font-size:10px">${tags.join(' · ')}</div>` : ''}
    `;

    // Position: offset from cursor, clamp to viewport
    const pad = 12;
    let left = clientX + pad;
    let top  = clientY + pad;
    if (left + 280 > window.innerWidth)  left = clientX - 280 - pad;
    if (top + 180 > window.innerHeight)  top  = clientY - 180 - pad;
    tooltipEl.style.left = left + 'px';
    tooltipEl.style.top  = top + 'px';
  }

  function hideBuildingTooltip(): void {
    if (tooltipTimer) { clearTimeout(tooltipTimer); tooltipTimer = null; }
    if (tooltipEl) { tooltipEl.remove(); tooltipEl = null; }
    lastHoveredNodeId = null;
  }

  // ── Drawing ───────────────────────────────────────────────────────────────

  function draw() {
    if (!graph) return;
    // Update legend when zoom crosses building threshold
    const isZoomed = CELL_SIZE * camera.zoom >= 12;
    if (isZoomed !== lastLegendZoomed) updateLegend(currentOverlay, isZoomed);
    const { grid_cols, grid_rows } = graph.meta;
    const cellW = WORLD_W / grid_cols;   // world units per sector
    const cellH = WORLD_H / grid_rows;

    // Viewport culling (in world coords)
    const vpL = -camera.x / camera.zoom;
    const vpT = -camera.y / camera.zoom;
    const vpR = vpL + CW / camera.zoom;
    const vpB = vpT + CH / camera.zoom;
    const colMin = Math.max(0, Math.floor(vpL / cellW) - 1);
    const colMax = Math.min(grid_cols - 1, Math.ceil(vpR / cellW));
    const rowMin = Math.max(0, Math.floor(vpT / cellH) - 1);
    const rowMax = Math.min(grid_rows - 1, Math.ceil(vpB / cellH));
    function isVisible(s: Sector) {
      return s.grid_col >= colMin && s.grid_col <= colMax &&
             s.grid_row >= rowMin && s.grid_row <= rowMax;
    }

    ctx.clearRect(0, 0, CW, CH);
    ctx.save();
    ctx.translate(camera.x, camera.y);
    ctx.scale(camera.zoom, camera.zoom);

    // ── L0: Dark concrete base ────────────────────────────────────────────
    ctx.fillStyle = '#080b12';
    ctx.fillRect(0, 0, WORLD_W, WORLD_H);

    // ── L1: Sector backgrounds (seamless grid cells) ───────────────────────
    for (const s of graph.sectors) {
      if (!isVisible(s)) continue;
      const x = s.grid_col * cellW, y = s.grid_row * cellH;
      ctx.fillStyle = TYPE_BG[s.sector_type] ?? '#111827';
      ctx.fillRect(x, y, cellW, cellH);
    }

    // ── L2: Overlay tints ─────────────────────────────────────────────────
    for (const s of graph.sectors) {
      if (!isVisible(s)) continue;
      const sx = s.grid_col * cellW, sy = s.grid_row * cellH;
      const live  = fixtureSectors.get(s.sector_id);
      const heat  = live?.heat ?? s.heat;
      const owner = live?.owner ?? null;
      let fill = 'transparent';
      switch (currentOverlay) {
        case 'heat': {
          const a = Math.round((heat / 100) * 0.55 * 255).toString(16).padStart(2,'0');
          fill = heatColor(heat) + a; break;
        }
        case 'ownership': {
          const own = owner ?? (aiSectors.has(s.sector_id) ? aiSectorName.get(s.sector_id)! : null);
          fill = own ? getOwnerColor(own, myCorpName) + '33' : 'transparent'; break;
        }
        case 'prosperity': {
          const a = Math.round((s.wealth / 100) * 0.4 * 255).toString(16).padStart(2,'0');
          fill = '#fca311' + a; break;
        }
        default:
          fill = (TYPE_STROKE[s.sector_type] ?? '#3a4a5a') + '55';
      }
      if (fill !== 'transparent') {
        ctx.fillStyle = fill;
        ctx.fillRect(sx, sy, cellW, cellH);
      }
    }

    // ── L2a: Fog of War — darken unscouted sectors ────────────────────────
    for (const s of graph.sectors) {
      if (!isVisible(s) || isScouted(s.sector_id)) continue;
      const sx = s.grid_col * cellW, sy = s.grid_row * cellH;
      ctx.fillStyle = 'rgba(4,6,12,0.70)';
      ctx.fillRect(sx, sy, cellW, cellH);
      // Diagonal hatch pattern for fog effect (skip if ctx.rect unavailable in test mocks)
      if (typeof ctx.rect === 'function') {
        ctx.save();
        ctx.beginPath();
        ctx.rect(sx, sy, cellW, cellH);
        ctx.clip();
        ctx.strokeStyle = accent(0.04);
        ctx.lineWidth = 1 / camera.zoom;
        const step = Math.max(8, 20 / camera.zoom);
        for (let d = -cellH; d < cellW + cellH; d += step) {
          ctx.moveTo(sx + d, sy);
          ctx.lineTo(sx + d - cellH, sy + cellH);
        }
        ctx.stroke();
        ctx.restore();
      }
    }

    // ── L2b: Heat prediction overlay ──────────────────────────────────────
    if (showHeatPrediction) {
      const HEAT_IMPACT: Record<string, number> = {
        SABOTAGE: 15, PATROL: 3, RECON: 5, INVEST: 2, LAUNDER: 8, BRIBE: 5,
      };
      const DANGER_THRESHOLD = 15;
      const plan: PlanEntryDTO[] = fixture.player?.plan ?? [];

      // Compute deltas per sector
      const newDeltas = new Map<string, number>();
      for (const entry of plan) {
        if (!entry.sector) continue;
        const impact = HEAT_IMPACT[entry.action] ?? 5;
        newDeltas.set(entry.sector, (newDeltas.get(entry.sector) ?? 0) + impact);
      }
      sectorHeatDeltas = newDeltas;

      for (const s of graph.sectors) {
        if (!isVisible(s)) continue;
        const delta = newDeltas.get(s.sector_id) ?? 0;
        if (delta <= 0) continue;
        const sx = s.grid_col * cellW, sy = s.grid_row * cellH;

        // Orange halo — opacity scales with delta (capped at 0.55)
        const opacity = Math.min(0.55, delta / 30);
        ctx.fillStyle = `rgba(252,163,17,${opacity.toFixed(2)})`;
        ctx.fillRect(sx, sy, cellW, cellH);

        // Red pulsing border for DANGER sectors
        if (delta >= DANGER_THRESHOLD) {
          ctx.strokeStyle = 'rgba(231,57,70,0.6)';
          ctx.lineWidth = 3 / camera.zoom;
          ctx.strokeRect(sx + 2 / camera.zoom, sy + 2 / camera.zoom,
                         cellW - 4 / camera.zoom, cellH - 4 / camera.zoom);
        }
      }
    }

    // ── L3: Grid lines (very subtle sector borders) ───────────────────────
    ctx.strokeStyle = accent(0.06);
    ctx.lineWidth = 1 / camera.zoom;
    for (const s of graph.sectors) {
      if (!isVisible(s)) continue;
      const x = s.grid_col * cellW, y = s.grid_row * cellH;
      ctx.strokeRect(x, y, cellW, cellH);
    }

    // ── L3b: Highway grid (AVENUEs at sector borders) — visible at all zoom levels
    {
      const hwWidth = Math.max(2, 6 / camera.zoom);
      ctx.strokeStyle = 'rgba(30,58,82,0.8)';
      ctx.lineWidth = hwWidth;
      for (const s of graph.sectors) {
        if (!isVisible(s)) continue;
        const x = s.grid_col * cellW, y = s.grid_row * cellH;
        ctx.strokeRect(x, y, cellW, cellH);
      }
      // Inner highlight line for highway feel
      ctx.strokeStyle = 'rgba(252,196,25,0.12)';
      ctx.lineWidth = Math.max(1, 2 / camera.zoom);
      for (const s of graph.sectors) {
        if (!isVisible(s)) continue;
        const x = s.grid_col * cellW, y = s.grid_row * cellH;
        ctx.strokeRect(x, y, cellW, cellH);
      }
    }

    // ── L4: AI corp dashed rings ──────────────────────────────────────────
    ctx.strokeStyle = 'rgba(122,58,170,0.6)';
    ctx.lineWidth = 4 / camera.zoom;
    ctx.setLineDash([TILE_SIZE * 0.5, TILE_SIZE * 0.3]);
    for (const s of graph.sectors) {
      if (!aiSectors.has(s.sector_id)) continue;
      const x = s.grid_col * cellW, y = s.grid_row * cellH;
      const m = TILE_SIZE * 0.3;
      ctx.strokeRect(x + m, y + m, cellW - 2 * m, cellH - 2 * m);
    }
    ctx.setLineDash([]);

    // ── L5: Road tiles — bitmask-based auto-tiling (top-down 2D) ────────────
    // Each STREET/AVENUE/ALLEY node gets a TILE_SIZE square.  The bitmask
    // selects the correct sprite variant (straight, corner, T, crossing) and
    // ctx.rotate() handles orientation.  Sidewalk pass stays as oversized
    // colored rects underneath for the visible concrete border.
    const TS = CELL_SIZE;  // roads fill their grid cell
    const halfT = TS / 2;
    // LOD: screen-pixels per tile (used for road + building sprite LOD)
    const tilePx = camera.zoom * CELL_SIZE;

    // Road width factors: AVENUE fills full cell, STREET 80%, ALLEY 50%
    const ROAD_WIDTHS: Record<string, number> = { AVENUE: 1.0, STREET: 0.80, ALLEY: 0.50 };
    const ROAD_FILL_ALLEY = '#0e1218';

    // Pass 1: Sidewalk tiles (slightly oversized, lighter concrete) — AVENUE + STREET only
    ctx.fillStyle = '#1e2636';
    const sidewalkPad = TS * 0.08;
    for (const s of graph.sectors) {
      if (!isVisible(s)) continue;
      for (const n of s.nodes) {
        if (n.node_type !== 'AVENUE' && n.node_type !== 'STREET') continue;
        const w = ROAD_WIDTHS[n.node_type] ?? 1;
        const rw = TS * w, rh = TS * w;
        const x = wx(n.global_x) - rw / 2 - sidewalkPad;
        const y = wy(n.global_y) - rh / 2 - sidewalkPad;
        ctx.fillRect(x, y, rw + sidewalkPad * 2, rh + sidewalkPad * 2);
      }
    }

    // Pass 2: Asphalt fill (batched by road type for fewer state changes)
    for (const s of graph.sectors) {
      if (!isVisible(s)) continue;
      for (const n of s.nodes) {
        if (n.node_type !== 'AVENUE' && n.node_type !== 'STREET' && n.node_type !== 'ALLEY') continue;
        const w = ROAD_WIDTHS[n.node_type] ?? 1;
        const rw = TS * w, rh = TS * w;
        ctx.fillStyle = n.node_type === 'AVENUE' ? ROAD_FILL_AVENUE
                       : n.node_type === 'ALLEY' ? ROAD_FILL_ALLEY
                       : ROAD_FILL_STREET;
        ctx.fillRect(wx(n.global_x) - rw / 2, wy(n.global_y) - rh / 2, rw, rh);
      }
    }

    // Pass 2b: Cross-sector road connectors (fill gap between gateway AVENUEs)
    if (crossConnectors.length > 0) {
      ctx.fillStyle = '#1e2636'; // sidewalk first
      for (const c of crossConnectors) {
        ctx.fillRect(c.wx - halfT - sidewalkPad, c.wy - halfT - sidewalkPad,
                     TS + sidewalkPad * 2, TS + sidewalkPad * 2);
      }
      ctx.fillStyle = ROAD_FILL_AVENUE; // asphalt
      for (const c of crossConnectors) {
        ctx.fillRect(c.wx - halfT, c.wy - halfT, TS, TS);
      }
    }

    // Pass 3: Procedural lane markings + curb edges (bitmask-driven, LOD gated)
    if (tilePx >= 12) {
      // 3a: Dashed center lane markings along road direction
      const laneW = Math.max(1.5, TS * 0.03);
      ctx.lineWidth = laneW;
      ctx.setLineDash([TS * 0.18, TS * 0.12]);
      for (const s of graph.sectors) {
        if (!isVisible(s)) continue;
        for (const n of s.nodes) {
          if (n.node_type !== 'AVENUE' && n.node_type !== 'STREET') continue;
          const mask = roadBitmasks.get(n.node_id) ?? 0;
          if (mask === 0) continue;
          const nx = wx(n.global_x), ny = wy(n.global_y);
          const isAve = n.node_type === 'AVENUE';
          ctx.strokeStyle = isAve
            ? 'rgba(252,196,25,0.35)' : 'rgba(180,210,230,0.16)';
          // Vertical lane (N/S connections)
          if (mask & (DIR_N | DIR_S)) {
            ctx.beginPath();
            ctx.moveTo(nx, (mask & DIR_N) ? ny - halfT : ny);
            ctx.lineTo(nx, (mask & DIR_S) ? ny + halfT : ny);
            ctx.stroke();
            // AVENUE: double lane (offset ±3px from center)
            if (isAve) {
              const off = TS * 0.06;
              ctx.beginPath();
              ctx.moveTo(nx - off, (mask & DIR_N) ? ny - halfT : ny);
              ctx.lineTo(nx - off, (mask & DIR_S) ? ny + halfT : ny);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(nx + off, (mask & DIR_N) ? ny - halfT : ny);
              ctx.lineTo(nx + off, (mask & DIR_S) ? ny + halfT : ny);
              ctx.stroke();
            }
          }
          // Horizontal lane (E/W connections)
          if (mask & (DIR_E | DIR_W)) {
            ctx.beginPath();
            ctx.moveTo((mask & DIR_W) ? nx - halfT : nx, ny);
            ctx.lineTo((mask & DIR_E) ? nx + halfT : nx, ny);
            ctx.stroke();
            if (isAve) {
              const off = TS * 0.06;
              ctx.beginPath();
              ctx.moveTo((mask & DIR_W) ? nx - halfT : nx, ny - off);
              ctx.lineTo((mask & DIR_E) ? nx + halfT : nx, ny - off);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo((mask & DIR_W) ? nx - halfT : nx, ny + off);
              ctx.lineTo((mask & DIR_E) ? nx + halfT : nx, ny + off);
              ctx.stroke();
            }
          }
        }
      }
      ctx.setLineDash([]);

      // 3b: Curb edges — highlight on sides WITHOUT a road neighbor.
      // This outlines the road network automatically from the bitmask data.
      ctx.strokeStyle = accent(0.14);
      ctx.lineWidth = Math.max(1.5, TS * 0.035);
      for (const s of graph.sectors) {
        if (!isVisible(s)) continue;
        for (const n of s.nodes) {
          if (n.node_type !== 'AVENUE' && n.node_type !== 'STREET') continue;
          const mask = roadBitmasks.get(n.node_id) ?? 0;
          const nx = wx(n.global_x), ny = wy(n.global_y);
          const l = nx - halfT, r = nx + halfT, t = ny - halfT, b = ny + halfT;
          ctx.beginPath();
          if (!(mask & DIR_N)) { ctx.moveTo(l, t); ctx.lineTo(r, t); }
          if (!(mask & DIR_E)) { ctx.moveTo(r, t); ctx.lineTo(r, b); }
          if (!(mask & DIR_S)) { ctx.moveTo(l, b); ctx.lineTo(r, b); }
          if (!(mask & DIR_W)) { ctx.moveTo(l, t); ctx.lineTo(l, b); }
          ctx.stroke();
        }
      }
    }

    // ── L6: City blocks — GOC-style contiguous building masses ──────────────
    const bldgS = CELL_SIZE * 0.98;      // nearly full cell for individual building tiles
    const halfS = bldgS / 2;
    const halfCell = CELL_SIZE / 2;

    // L6a: Block fills — render each block as a solid filled area
    if (cityBlocks && tilePx >= 2) {
      for (const block of cityBlocks) {
        if (block.sectorCol < colMin || block.sectorCol > colMax ||
            block.sectorRow < rowMin || block.sectorRow > rowMax) continue;

        // Fill each cell of the block individually (handles irregular shapes)
        for (const cell of block.cells) {
          if (cell.type === 'COURTYARD') {
            // Courtyard = enclosed interior, visible as paved inner yard
            ctx.fillStyle = '#1a2030'; // lighter concrete/pavement tone
          } else if (cell.type === 'ALLEY') {
            // Alley = vertical passage through block
            ctx.fillStyle = '#0e1420'; // dark passage
          } else {
            // Building cells — dark base for roof
            ctx.fillStyle = '#161e2c';
          }
          ctx.fillRect(cell.wx - halfCell, cell.wy - halfCell, CELL_SIZE, CELL_SIZE);
        }

        // Block outline (facade) — thick border around the block's outer edges
        if (tilePx >= 6) {
          ctx.strokeStyle = '#2a3648';
          ctx.lineWidth = Math.max(2, 3 / camera.zoom);
          // Draw outline by checking each cell's edges: if neighbor is NOT in block → draw edge
          const cellSet = new Set(block.cells.map(c => `${c.row},${c.col}`));
          for (const cell of block.cells) {
            if (cell.type === 'ALLEY' || cell.type === 'COURTYARD') continue; // only buildings have facades
            const cx = cell.wx, cy = cell.wy;
            const l = cx - halfCell, r = cx + halfCell, t = cy - halfCell, b = cy + halfCell;
            ctx.beginPath();
            if (!cellSet.has(`${cell.row - 1},${cell.col}`)) { ctx.moveTo(l, t); ctx.lineTo(r, t); }
            if (!cellSet.has(`${cell.row},${cell.col + 1}`)) { ctx.moveTo(r, t); ctx.lineTo(r, b); }
            if (!cellSet.has(`${cell.row + 1},${cell.col}`)) { ctx.moveTo(l, b); ctx.lineTo(r, b); }
            if (!cellSet.has(`${cell.row},${cell.col - 1}`)) { ctx.moveTo(l, t); ctx.lineTo(l, b); }
            ctx.stroke();
          }
          // Sidewalk strip: lighter line just outside the facade
          ctx.strokeStyle = '#1e2636';
          ctx.lineWidth = Math.max(1, 2 / camera.zoom);
          for (const cell of block.cells) {
            if (cell.type === 'ALLEY' || cell.type === 'COURTYARD') continue;
            const cx = cell.wx, cy = cell.wy;
            const pad = 1 / camera.zoom;
            const l = cx - halfCell - pad, r = cx + halfCell + pad;
            const t = cy - halfCell - pad, b = cy + halfCell + pad;
            ctx.beginPath();
            if (!cellSet.has(`${cell.row - 1},${cell.col}`)) { ctx.moveTo(l, t); ctx.lineTo(r, t); }
            if (!cellSet.has(`${cell.row},${cell.col + 1}`)) { ctx.moveTo(r, t); ctx.lineTo(r, b); }
            if (!cellSet.has(`${cell.row + 1},${cell.col}`)) { ctx.moveTo(l, b); ctx.lineTo(r, b); }
            if (!cellSet.has(`${cell.row},${cell.col - 1}`)) { ctx.moveTo(l, t); ctx.lineTo(l, b); }
            ctx.stroke();
          }
        }
      }
    }

    // L6a-2: Courtyard detail — paving pattern at high zoom
    if (cityBlocks && tilePx >= 16) {
      for (const block of cityBlocks) {
        if (block.sectorCol < colMin || block.sectorCol > colMax ||
            block.sectorRow < rowMin || block.sectorRow > rowMax) continue;
        for (const cell of block.cells) {
          if (cell.type !== 'COURTYARD') continue;
          const cx = cell.wx, cy = cell.wy;
          // Paving stone grid pattern
          ctx.strokeStyle = 'rgba(255,255,255,0.04)';
          ctx.lineWidth = 0.5 / camera.zoom;
          const step = CELL_SIZE / 4;
          for (let i = 1; i < 4; i++) {
            ctx.beginPath();
            ctx.moveTo(cx - halfCell + step * i, cy - halfCell);
            ctx.lineTo(cx - halfCell + step * i, cy + halfCell);
            ctx.moveTo(cx - halfCell, cy - halfCell + step * i);
            ctx.lineTo(cx + halfCell, cy - halfCell + step * i);
            ctx.stroke();
          }
          // Shadow edge from surrounding buildings (inner shadow)
          const grad = ctx.createRadialGradient(cx, cy, CELL_SIZE * 0.1, cx, cy, CELL_SIZE * 0.6);
          grad.addColorStop(0, 'rgba(0,0,0,0)');
          grad.addColorStop(1, 'rgba(0,0,0,0.25)');
          ctx.fillStyle = grad;
          ctx.fillRect(cx - halfCell, cy - halfCell, CELL_SIZE, CELL_SIZE);
        }
      }
    }

    // L6b: Individual building detail — roof textures at high zoom (LOD 3+)
    if (sortedBuildings && tilePx >= 12) {
      for (const b of sortedBuildings) {
        if (b.sectorCol < colMin || b.sectorCol > colMax ||
            b.sectorRow < rowMin || b.sectorRow > rowMax) continue;

        // Individual building tile within the block
        if (tilePx >= 20 && spritesReady) {
          // Footprint cells: tint overlay only, no sprite
          if (!b.isFootprint) {
            const img = roofImages.get(b.tileIdx);
            if (img) {
              const facingRot: Record<string, number> = { N: 0, E: Math.PI / 2, S: Math.PI, W: -Math.PI / 2 };
              const rot = facingRot[b.facing] ?? 0;
              ctx.save();
              ctx.translate(b.wx, b.wy);
              if (rot !== 0) ctx.rotate(rot);
              ctx.drawImage(img, 0, 0, img.width, img.height, -halfS, -halfS, bldgS, bldgS);
              ctx.restore();
            }
          }
          // Category tint overlay (always, even if sprite missing)
          ctx.fillStyle = b.tint + (b.isFootprint ? '40' : '30');
          ctx.fillRect(b.wx - halfCell, b.wy - halfCell, CELL_SIZE, CELL_SIZE);
        } else {
          // LOD 2: category-tinted cell within the block
          ctx.fillStyle = b.tint + '50';
          ctx.fillRect(b.wx - halfCell, b.wy - halfCell, CELL_SIZE, CELL_SIZE);
        }

        // Thin inner grid line between buildings (subtle separation within block)
        ctx.strokeStyle = 'rgba(10,14,20,0.3)';
        ctx.lineWidth = 0.5;
        ctx.strokeRect(b.wx - halfCell, b.wy - halfCell, CELL_SIZE, CELL_SIZE);

        // Neon glow for nightlife/cyber categories (high zoom only)
        const glow = NEON_GLOW[b.cat];
        if (glow && tilePx >= 20) {
          ctx.save();
          ctx.shadowColor = glow;
          ctx.shadowBlur = 6 / camera.zoom;
          ctx.strokeStyle = glow + '40';
          ctx.lineWidth = 1.5 / camera.zoom;
          ctx.strokeRect(b.wx - halfCell, b.wy - halfCell, CELL_SIZE, CELL_SIZE);
          ctx.restore();
        }
      }
    } else if (!sortedBuildings) {
      // Fallback before world_buildings loads
      for (const s of graph.sectors) {
        if (!isVisible(s) || !showNodes) continue;
        for (const n of s.nodes) {
          if (n.node_type !== 'BUILDING_ENTRY') continue;
          const sz = TILE_SIZE * 0.4;
          ctx.fillStyle = accent(1);
          ctx.fillRect(wx(n.global_x) - sz / 2, wy(n.global_y) - sz / 2, sz, sz);
        }
      }
    }

    // ── L6b: Selected + focused building highlights ─────────────────────────
    if (sortedBuildings && tilePx >= 4) {
      const accent = getAccentColor();
      // Multi-select highlights (Shift+Click)
      if (selectedBldgs.size > 0) {
        ctx.save();
        ctx.shadowColor = accent;
        ctx.shadowBlur = 8 / camera.zoom;
        ctx.strokeStyle = accent;
        ctx.lineWidth = 2 / camera.zoom;
        for (const b of sortedBuildings) {
          if (!selectedBldgs.has(b.nodeId)) continue;
          ctx.strokeRect(b.wx - halfS, b.wy - halfS, bldgS, bldgS);
        }
        ctx.restore();
      }
      // Focused building highlight (single click → detail panel)
      if (focusedBldgId) {
        const fb = sortedBuildings.find(b => b.nodeId === focusedBldgId);
        if (fb) {
          ctx.save();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5 / camera.zoom;
          ctx.shadowColor = accent;
          ctx.shadowBlur = 12 / camera.zoom;
          ctx.strokeRect(fb.wx - halfS - 1, fb.wy - halfS - 1, bldgS + 2, bldgS + 2);
          ctx.restore();
        }
      }
    }

    // ── L6c: Lasso rectangle ────────────────────────────────────────────
    if (isLassoing && lassoStartWorld && lassoEndWorld) {
      const lx = Math.min(lassoStartWorld.x, lassoEndWorld.x);
      const ly = Math.min(lassoStartWorld.y, lassoEndWorld.y);
      const lw = Math.abs(lassoEndWorld.x - lassoStartWorld.x);
      const lh = Math.abs(lassoEndWorld.y - lassoStartWorld.y);
      ctx.save();
      ctx.fillStyle = accent(0.08);
      ctx.fillRect(lx, ly, lw, lh);
      ctx.strokeStyle = accent(0.6);
      ctx.lineWidth = 2 / camera.zoom;
      ctx.setLineDash([6 / camera.zoom, 4 / camera.zoom]);
      ctx.strokeRect(lx, ly, lw, lh);
      ctx.setLineDash([]);
      ctx.restore();
    }

    // ── L6d: Player-owned buildings — ALWAYS visible ──────────────────────
    {
      const playerBldgs = fixture.player?.buildings ?? [];
      const ownedSectors = new Set(playerBldgs.map((b: { sector: string }) => b.sector));
      const ownedNodeIds = new Set(playerBldgs.map((b: { nodeId: string }) => b.nodeId));

      if (ownedSectors.size > 0) {
        const ownedAccent = getAccentColor();
        // Zoomed out: highlight owned SECTORS with accent border
        if (tilePx < 8) {
          ctx.save();
          ctx.strokeStyle = ownedAccent;
          ctx.lineWidth = 3 / camera.zoom;
          ctx.shadowColor = ownedAccent;
          ctx.shadowBlur = 8 / camera.zoom;
          ctx.setLineDash([8 / camera.zoom, 4 / camera.zoom]);
          for (const s of graph.sectors) {
            if (!ownedSectors.has(s.sector_id)) continue;
            const sx = s.grid_col * cellW, sy = s.grid_row * cellH;
            ctx.strokeRect(sx + 3 / camera.zoom, sy + 3 / camera.zoom,
                           cellW - 6 / camera.zoom, cellH - 6 / camera.zoom);
            // Sector label badge
            const count = playerBldgs.filter((b: { sector: string }) => b.sector === s.sector_id).length;
            const fontSize = Math.max(6, 14 / camera.zoom);
            ctx.font = `bold ${fontSize}px monospace`;
            ctx.fillStyle = ownedAccent;
            ctx.textAlign = 'center';
            ctx.fillText(t('map.bldg_count', { count: String(count) }), sx + cellW / 2, sy + cellH / 2 + fontSize / 3);
          }
          ctx.setLineDash([]);
          ctx.restore();
        }

        // Zoomed in: highlight individual buildings with thick amber outline + glow
        if (sortedBuildings && tilePx >= 8) {
          ctx.save();
          ctx.strokeStyle = ownedAccent;
          ctx.lineWidth = 2.5 / camera.zoom;
          ctx.shadowColor = ownedAccent;
          ctx.shadowBlur = 6 / camera.zoom;
          for (const b of sortedBuildings) {
            const bldg = worldBldgs?.get(b.nodeId);
            if (bldg && ownedNodeIds.has(b.nodeId)) {
              ctx.strokeRect(b.wx - halfS - 1, b.wy - halfS - 1, bldgS + 2, bldgS + 2);
              // Small "$" badge
              if (tilePx >= 16) {
                ctx.font = `bold ${Math.max(6, TILE_SIZE * 0.4)}px monospace`;
                ctx.fillStyle = ownedAccent;
                ctx.textAlign = 'center';
                ctx.fillText('$', b.wx, b.wy + TILE_SIZE * 0.55);
              }
            }
          }
          ctx.restore();
        }
      }
    }

    // ── L6e: HQ marker — star on COURTYARD node position ──────────────────
    {
      const hq = fixture.player?.hq;
      let hx = 0, hy = 0;

      // Resolve HQ position from the COURTYARD node itself (not the front building)
      if (hq?.nodeId && graph) {
        for (const s of graph.sectors) {
          for (const n of s.nodes) {
            if (n.node_id === hq.nodeId) {
              hx = wx(n.global_x);
              hy = wy(n.global_y);
              break;
            }
          }
          if (hx > 0) break;
        }
      }

      // Fallback: find is_hq node in player's sector
      if (hx === 0 && hq?.sectorId && graph) {
        for (const s of graph.sectors) {
          if (s.sector_id !== hq.sectorId) continue;
          for (const n of s.nodes) {
            if (n.is_hq) {
              hx = wx(n.global_x);
              hy = wy(n.global_y);
              break;
            }
          }
          break;
        }
      }

      // Final fallback: fixture worldX/worldY
      if (hx === 0 && hy === 0 && hq && hq.worldX > 0 && hq.worldY > 0) {
        hx = wx(hq.worldX);
        hy = wy(hq.worldY);
      }

      if (hx > 0 && hy > 0) {
        ctx.save();
        const accent = getAccentColor();

        // Draw 5-point star at HQ courtyard position
        const drawStar = (cx: number, cy: number, r: number, spikes: number) => {
          const innerR = r * 0.45;
          ctx.beginPath();
          for (let i = 0; i < spikes * 2; i++) {
            const angle = (i * Math.PI) / spikes - Math.PI / 2;
            const radius = i % 2 === 0 ? r : innerR;
            const px = cx + Math.cos(angle) * radius;
            const py = cy + Math.sin(angle) * radius;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
        };

        if (tilePx >= 8) {
          // Building zoom: star on courtyard tile
          const starR = Math.max(TILE_SIZE * 0.5, 6 / camera.zoom);
          ctx.fillStyle = accent;
          ctx.shadowColor = accent;
          ctx.shadowBlur = 10 / camera.zoom;
          drawStar(hx, hy, starR, 5);
          ctx.fill();
          // HQ label
          const badgeFontSize = Math.max(8, TILE_SIZE * 0.35);
          ctx.font = `bold ${badgeFontSize}px monospace`;
          ctx.fillStyle = accent;
          ctx.textAlign = 'center';
          ctx.shadowBlur = 4 / camera.zoom;
          ctx.fillText('HQ', hx, hy - starR - 4 / camera.zoom);
        } else {
          // Sector zoom: larger star beacon
          const minScreenPx = 20;
          const starR = Math.max(TILE_SIZE * 1.5, minScreenPx / camera.zoom * 0.5);
          ctx.fillStyle = accent;
          ctx.shadowColor = accent;
          ctx.shadowBlur = 12 / camera.zoom;
          drawStar(hx, hy, starR, 5);
          ctx.fill();
          // Label
          const fontSize = Math.max(8, Math.min(18, 18 / camera.zoom));
          ctx.font = `bold ${fontSize}px monospace`;
          ctx.fillStyle = accent;
          ctx.textAlign = 'center';
          ctx.shadowBlur = 0;
          ctx.fillText('HQ', hx, hy - starR - 6 / camera.zoom);
        }

        ctx.restore();
      }
    }

    // ── L6f: Filter highlights — dim non-matching, glow matching ────────────
    if (isAnyFilterActive() && sortedBuildings) {
      ctx.save();
      // Dim non-matching buildings with semi-transparent overlay
      for (const b of sortedBuildings) {
        const fid = getNodeFilter(b.nodeId);
        if (!fid) {
          // Dim this building
          ctx.fillStyle = 'rgba(4, 6, 12, 0.65)';
          ctx.fillRect(b.wx - halfS - 1, b.wy - halfS - 1, bldgS + 2, bldgS + 2);
        }
      }
      // Highlight matching buildings with filter color outline + glow
      for (const b of sortedBuildings) {
        const fid = getNodeFilter(b.nodeId);
        if (fid) {
          const color = getFilterColor(fid);
          ctx.strokeStyle = color;
          ctx.lineWidth = 2 / camera.zoom;
          ctx.shadowColor = color;
          ctx.shadowBlur = 8 / camera.zoom;
          ctx.strokeRect(b.wx - halfS - 1, b.wy - halfS - 1, bldgS + 2, bldgS + 2);
        }
      }
      // Zoomed out: sector-level badges with match count
      if (tilePx < 12 && graph) {
        const sectorCounts = new Map<string, number>();
        for (const b of sortedBuildings) {
          if (getNodeFilter(b.nodeId)) {
            sectorCounts.set(b.sectorId, (sectorCounts.get(b.sectorId) ?? 0) + 1);
          }
        }
        const fontSize = Math.max(6, 12 / camera.zoom);
        ctx.font = `bold ${fontSize}px monospace`;
        ctx.textAlign = 'center';
        for (const [sid, count] of sectorCounts) {
          const bounds = sectorBounds.get(sid);
          if (!bounds) continue;
          const cx = bounds.x + bounds.w / 2;
          const cy = bounds.y + bounds.h / 2;
          // Background pill
          const tw = ctx.measureText(`${count}`).width + 6 / camera.zoom;
          ctx.fillStyle = accent(0.15);
          ctx.fillRect(cx - tw / 2, cy - fontSize / 2 - 2 / camera.zoom, tw, fontSize + 4 / camera.zoom);
          // Text
          ctx.fillStyle = accent(1);
          ctx.fillText(`${count}`, cx, cy + fontSize / 3);
        }
      }
      ctx.restore();
    }

    // ── L6g: Plan visualization — show planned actions on buildings/sectors ─
    {
      // In plan mode, show local edits; otherwise show server plan
      const plan = isPlanModeActive() ? [...getLocalPlan()] : (fixture.player?.plan ?? []);
      if (plan.length > 0 && sortedBuildings) {

        // Group plan entries by sector
        const bySector = new Map<string, typeof plan>();
        for (const entry of plan) {
          const arr = bySector.get(entry.sector) ?? [];
          arr.push(entry);
          bySector.set(entry.sector, arr);
        }

        if (tilePx >= 12) {
          // Building-level: mark individual buildings with colored ring + glyph
          for (const entry of plan) {
            const vis = ACTION_VIS[entry.action.toLowerCase()] ?? { color: '#ffffff', glyph: '•' };
            // Match target building or first building in target sector
            const targets = entry.target
              ? sortedBuildings.filter(b => b.nodeId === entry.target)
              : sortedBuildings.filter(b => b.sectorId === entry.sector).slice(0, 1);

            for (const b of targets) {
              ctx.save();
              const r = TILE_SIZE * 0.55;
              // Ring
              ctx.strokeStyle = vis.color;
              ctx.lineWidth = Math.max(1.5, 2 / camera.zoom);
              ctx.globalAlpha = 0.8;
              ctx.beginPath();
              ctx.arc(b.wx, b.wy, r, 0, Math.PI * 2);
              ctx.stroke();
              // Glyph
              const fontSize = Math.max(8, TILE_SIZE * 0.5);
              ctx.font = `bold ${fontSize}px monospace`;
              ctx.fillStyle = vis.color;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.globalAlpha = 1;
              ctx.fillText(vis.glyph, b.wx, b.wy - r - fontSize * 0.6);
              ctx.restore();
            }

            // PATROL special: semi-transparent overlay over sector
            if (entry.action.toLowerCase() === 'patrol') {
              const sectorInfo = graph.sectors.find(s => s.sector_id === entry.sector);
              if (sectorInfo) {
                ctx.save();
                ctx.fillStyle = 'rgba(6,214,160,0.06)';
                const sx = sectorInfo.grid_col * cellW;
                const sy = sectorInfo.grid_row * cellH;
                ctx.fillRect(sx, sy, cellW, cellH);
                ctx.restore();
              }
            }
          }
        } else {
          // Sector-level: badge with count + dominant color
          for (const [sectorId, entries] of bySector) {
            const sectorInfo = graph.sectors.find(s => s.sector_id === sectorId);
            if (!sectorInfo || !isVisible(sectorInfo)) continue;
            const sx = sectorInfo.grid_col * cellW;
            const sy = sectorInfo.grid_row * cellH;
            const sw = cellW;

            // Dominant color = most frequent action type
            const typeCounts = new Map<string, number>();
            for (const e of entries) {
              const key = e.action.toLowerCase();
              typeCounts.set(key, (typeCounts.get(key) ?? 0) + 1);
            }
            let dominant = '';
            let maxC = 0;
            for (const [k, v] of typeCounts) { if (v > maxC) { maxC = v; dominant = k; } }
            const color = ACTION_VIS[dominant]?.color ?? '#ffffff';

            ctx.save();
            const badgeR = Math.max(6, 10 / camera.zoom);
            const bx = sx + sw - badgeR * 1.5;
            const by = sy + badgeR * 1.5;
            ctx.fillStyle = color;
            ctx.globalAlpha = 0.9;
            ctx.beginPath();
            ctx.arc(bx, by, badgeR, 0, Math.PI * 2);
            ctx.fill();
            // Count label
            const fs = Math.max(6, badgeR * 1.2);
            ctx.font = `bold ${fs}px monospace`;
            ctx.fillStyle = '#000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(entries.length), bx, by);
            ctx.restore();
          }
        }
      }
    }

    // ── L7: Road/border nodes — only at high zoom ─────────────────────────
    if (showNodes && tilePx >= 24) {
      const ns = TILE_SIZE * 0.15;
      for (const s of graph.sectors) {
        if (!isVisible(s)) continue;
        for (const n of s.nodes) {
          if (n.node_type === 'BUILDING_ENTRY' || n.node_type === 'BUILDING_FOOTPRINT') continue;
          const x = wx(n.global_x), y = wy(n.global_y);
          switch (n.node_type) {
            case 'AVENUE':
              ctx.fillStyle = 'rgba(160,190,230,0.3)';
              ctx.fillRect(x - ns, y - ns, ns * 2, ns * 2);
              break;
            case 'STREET':
              ctx.fillStyle = 'rgba(130,160,200,0.25)';
              ctx.fillRect(x - ns * 0.7, y - ns * 0.7, ns * 1.4, ns * 1.4);
              break;
            case 'BORDER': {
              const r = TILE_SIZE * 0.2;
              ctx.fillStyle = 'rgba(160,180,200,0.3)';
              ctx.beginPath();
              ctx.moveTo(x, y - r); ctx.lineTo(x + r, y);
              ctx.lineTo(x, y + r); ctx.lineTo(x - r, y);
              ctx.closePath(); ctx.fill();
              break;
            }
          }
        }
      }
    }

    // ── L8: Selection highlight ───────────────────────────────────────────
    if (selectedId) {
      const s = sectorMap.get(selectedId);
      if (s) {
        const x = s.grid_col * cellW, y = s.grid_row * cellH;
        ctx.strokeStyle = accent(1);
        ctx.lineWidth = 4 / camera.zoom;
        ctx.shadowColor = accent(1);
        ctx.shadowBlur = 12 / camera.zoom;
        ctx.strokeRect(x + 4, y + 4, cellW - 8, cellH - 8);
        ctx.shadowBlur = 0;
      }
    }

    // ── L9: Sector labels — fade out when zoomed in to building level ──────
    {
      const labelTilePx = CELL_SIZE * camera.zoom;
      // Fully visible below tilePx 12, fade out between 12-20, hidden above 20
      const labelOpacity = labelTilePx < 12 ? 1 : labelTilePx > 20 ? 0 : 1 - (labelTilePx - 12) / 8;
      if (labelOpacity > 0.01) {
        const fontSize = Math.max(8, Math.min(TILE_SIZE * 2.5, 80 / camera.zoom));
        ctx.font = `bold ${fontSize}px JetBrains Mono, monospace`;
        ctx.textAlign = 'center';
        for (const s of graph.sectors) {
          const cx = (s.grid_col + 0.5) * cellW;
          const cy = (s.grid_row + 0.5) * cellH;
          const fogDim = isScouted(s.sector_id) ? 1 : 0.35;
          ctx.fillStyle = `rgba(255,255,255,${(0.5 * labelOpacity * fogDim).toFixed(2)})`;
          ctx.fillText(s.sector_id, cx, cy - fontSize * 0.3);
          ctx.fillStyle = `rgba(255,255,255,${(0.3 * labelOpacity * fogDim).toFixed(2)})`;
          ctx.font = `${Math.max(6, fontSize * 0.7)}px Space Grotesk, system-ui, sans-serif`;
          ctx.fillText(s.name, cx, cy + fontSize * 0.7);
          ctx.font = `bold ${fontSize}px JetBrains Mono, monospace`;
        }
      }
    }

    ctx.restore();
  }

  // ── Sprite preloading ─────────────────────────────────────────────────────

  function preloadSprites(): Promise<void> {
    const promises = ROOF_TEXTURES.map((name, idx) => new Promise<void>(resolve => {
      const img = new Image();
      img.onload  = () => { roofImages.set(idx, img); resolve(); };
      img.onerror = () => { console.warn(`${name}.png failed to load`); resolve(); };
      img.src = resolveAsset(`map-sprites/${name}`);
    }));
    return Promise.all(promises).then(() => { spritesReady = true; });
  }

  // ── Road bitmask computation (runs once after city_graph loads) ─────────

  function buildRoadBitmasks(sectors: Sector[]): Map<string, number> {
    // Step 1: Detect actual node spacing from first sector's avenue nodes.
    // Engine places nodes at ~60-unit intervals (sector_width / positions_per_row),
    // which differs from TILE_SIZE (64) used for visual tile size.
    let step = 60;
    for (const s of sectors) {
      const xs = s.nodes
        .filter(n => n.node_type === 'AVENUE')
        .map(n => wx(n.global_x))
        .sort((a, b) => a - b);
      for (let i = 1; i < xs.length; i++) {
        const d = xs[i] - xs[i - 1];
        if (d > 10) { step = Math.round(d); break; }
      }
      if (step !== 60) break;
    }

    // Step 2: Build grid lookup (all road types participate in connectivity)
    const ROAD_TYPES = new Set(['AVENUE', 'STREET', 'ALLEY']);
    const grid = new Map<string, string>();   // "gx,gy" → node_id
    const gridCoords = new Map<string, string>(); // node_id → "gx,gy"
    for (const s of sectors) {
      for (const n of s.nodes) {
        if (!ROAD_TYPES.has(n.node_type)) continue;
        const gx = Math.round(wx(n.global_x) / step);
        const gy = Math.round(wy(n.global_y) / step);
        const key = `${gx},${gy}`;
        grid.set(key, n.node_id);
        gridCoords.set(n.node_id, key);
      }
    }

    // Step 3: Compute bitmask for each road node
    const masks = new Map<string, number>();
    for (const [key, nodeId] of grid) {
      const [gx, gy] = key.split(',').map(Number);
      let mask = 0;
      if (grid.has(`${gx},${gy - 1}`)) mask |= DIR_N;
      if (grid.has(`${gx + 1},${gy}`)) mask |= DIR_E;
      if (grid.has(`${gx},${gy + 1}`)) mask |= DIR_S;
      if (grid.has(`${gx - 1},${gy}`)) mask |= DIR_W;
      masks.set(nodeId, mask);
    }
    return masks;
  }

  // ── Cross-sector road connector computation ──────────────────────────────
  function buildCrossConnectors(sectors: Sector[]): RoadConnector[] {
    const conns: RoadConnector[] = [];
    const seen = new Set<string>();
    for (const s of sectors) {
      for (const e of s.edges) {
        if (e.edge_type !== 'cross_sector') continue;
        const a = nodeMap.get(e.from), b = nodeMap.get(e.to);
        if (!a || !b) continue;
        if (a.node_type !== 'AVENUE' || b.node_type !== 'AVENUE') continue;
        // Deduplicate (both sectors may list the same edge)
        const key = [e.from, e.to].sort().join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        const mx = (wx(a.global_x) + wx(b.global_x)) / 2;
        const my = (wy(a.global_y) + wy(b.global_y)) / 2;
        const horizontal = Math.abs(a.global_x - b.global_x) > Math.abs(a.global_y - b.global_y);
        conns.push({ wx: mx, wy: my, horizontal });
      }
    }
    return conns;
  }

  // ── Pan/Zoom events ───────────────────────────────────────────────────────

  // Shift-key cursor styling
  document.addEventListener('keydown', e => {
    if (e.key === 'Shift' && TILE_SIZE * camera.zoom >= 12) canvas.style.cursor = 'crosshair';
  });
  document.addEventListener('keyup', e => {
    if (e.key === 'Shift') canvas.style.cursor = '';
  });

  canvas.addEventListener('mousedown', e => {
    // Shift+Drag = Lasso (only in building zoom)
    if (e.shiftKey && TILE_SIZE * camera.zoom >= 12 && e.button === 0) {
      isLassoing = true;
      const r = canvas.getBoundingClientRect();
      lassoStartWorld = toWorld(e.clientX - r.left, e.clientY - r.top);
      lassoEndWorld = { ...lassoStartWorld };
      return; // don't start pan
    }
    isPanning = true;
    lastMX = e.clientX;
    lastMY = e.clientY;
    canvas.classList.add('panning');
  });

  canvas.addEventListener('mousemove', e => {
    // Lasso drag
    if (isLassoing) {
      const r = canvas.getBoundingClientRect();
      lassoEndWorld = toWorld(e.clientX - r.left, e.clientY - r.top);
      draw();
      return;
    }

    if (isPanning) {
      const r = canvas.getBoundingClientRect();
      camera.x += (e.clientX - lastMX) * CW / r.width;
      camera.y += (e.clientY - lastMY) * CH / r.height;
      lastMX = e.clientX;
      lastMY = e.clientY;
      clampPan();
      draw();
      hideBuildingTooltip();
      return;
    }

    // Building hover tooltip + cursor (non-panning)
    const r = canvas.getBoundingClientRect();
    const w = toWorld(e.clientX - r.left, e.clientY - r.top);
    const b = buildingAt(w.x, w.y);
    // Cursor feedback: pointer on building, crosshair when shift held
    if (!e.shiftKey) canvas.style.cursor = b ? 'pointer' : 'grab';
    if (b && b.nodeId !== lastHoveredNodeId) {
      hideBuildingTooltip();
      lastHoveredNodeId = b.nodeId;
      tooltipTimer = setTimeout(() => showBuildingTooltip(b, e.clientX, e.clientY), 300);
    } else if (!b && lastHoveredNodeId) {
      hideBuildingTooltip();
    } else if (b && b.nodeId === lastHoveredNodeId && tooltipEl) {
      // Update position while hovering same building
      const pad = 12;
      let left = e.clientX + pad;
      let top  = e.clientY + pad;
      if (left + 240 > window.innerWidth)  left = e.clientX - 240 - pad;
      if (top + 100 > window.innerHeight) top  = e.clientY - 100 - pad;
      tooltipEl.style.left = left + 'px';
      tooltipEl.style.top  = top + 'px';
    }
  });

  canvas.addEventListener('mouseup', e => {
    // Lasso end
    if (isLassoing && lassoStartWorld && lassoEndWorld) {
      isLassoing = false;
      selectBuildingsInRect(lassoStartWorld.x, lassoStartWorld.y, lassoEndWorld.x, lassoEndWorld.y);
      lassoStartWorld = null;
      lassoEndWorld = null;
      draw();
      showFab();
      return;
    }
    if (!isPanning) return;
    isPanning = false;
    canvas.classList.remove('panning');
    if (e.button === 0) handleClick(e);
  });
  canvas.addEventListener('mouseleave', () => { isPanning = false; canvas.classList.remove('panning'); hideBuildingTooltip(); });

  canvas.addEventListener('wheel', e => {
    e.preventDefault();
    const r = canvas.getBoundingClientRect();
    const mx = (e.clientX - r.left) * CW / r.width;
    const my = (e.clientY - r.top)  * CH / r.height;
    const factor = e.deltaY < 0 ? 1.15 : 0.87;
    const newZoom = Math.min(Math.max(camera.zoom * factor, INIT_ZOOM * 0.5), 2.0);
    camera.x = mx - (mx - camera.x) * (newZoom / camera.zoom);
    camera.y = my - (my - camera.y) * (newZoom / camera.zoom);
    camera.zoom = newZoom;
    clampPan();
    draw();
  }, { passive: false });

  let clickStartX = 0, clickStartY = 0;
  canvas.addEventListener('mousedown', e => { clickStartX = e.clientX; clickStartY = e.clientY; });

  function handleClick(e: MouseEvent) {
    const dx = Math.abs(e.clientX - clickStartX);
    const dy = Math.abs(e.clientY - clickStartY);
    if (dx > 4 || dy > 4) return; // was a drag, not click
    const r = canvas.getBoundingClientRect();
    const w = toWorld(e.clientX - r.left, e.clientY - r.top);

    // Building-level interaction (only in building zoom)
    const tilePx = CELL_SIZE * camera.zoom;
    if (tilePx >= 12) {
      const b = buildingAt(w.x, w.y);
      if (b) {
        const multiSelect = (e.shiftKey || e.ctrlKey || e.metaKey) && isBuildingSelectable(b.nodeId);
        if (multiSelect) {
          // Shift/Ctrl/Cmd+Click: toggle multi-select for route planning
          if (selectedBldgs.has(b.nodeId)) {
            selectedBldgs.delete(b.nodeId);
          } else {
            if (selectedBldgs.size > 0) {
              const existingSector = sortedBuildings?.find(sb => selectedBldgs.has(sb.nodeId))?.sectorId;
              if (existingSector && existingSector !== b.sectorId) selectedBldgs.clear();
            }
            selectedBldgs.add(b.nodeId);
          }
          draw();
          if (selectedBldgs.size > 0) showFab(); else removeFab();
        } else {
          // Single click: show building detail panel
          clearSelection();
          focusedBldgId = b.nodeId;
          showBuildingPanel(b);
          draw();
        }
        return;
      }
      // Click on empty space → clear selection + focus, back to sector
      if (selectedBldgs.size > 0 || focusedBldgId) {
        clearSelection();
        focusedBldgId = null;
        draw();
        return;
      }
    }

    const s = sectorAt(w.x, w.y);
    if (s) {
      selectedId = s.sector_id;
      focusedBldgId = null;
      showSectorPanel(s.sector_id);
      draw();
      // Plan mode: sector click is handled by context menu now
    }
  }

  // ── Context Menu (Right Click) ────────────────────────────────────────────
  let contextMenuEl: HTMLElement | null = null;

  canvas.addEventListener('contextmenu', e => {
    e.preventDefault();
    const dx = Math.abs(e.clientX - clickStartX);
    const dy = Math.abs(e.clientY - clickStartY);
    if (dx > 4 || dy > 4) return; // was a drag, not click

    if (contextMenuEl) {
      contextMenuEl.remove();
      contextMenuEl = null;
    }

    const w = toWorld(e.clientX - canvas.getBoundingClientRect().left, e.clientY - canvas.getBoundingClientRect().top);
    const s = sectorAt(w.x, w.y);
    if (!s) return;

    selectedId = s.sector_id;
    showSectorPanel(s.sector_id);
    draw();

    contextMenuEl = document.createElement('div');
    contextMenuEl.style.cssText = `
      position: fixed;
      left: ${e.clientX}px;
      top: ${e.clientY}px;
      background: rgba(10, 12, 20, 0.95);
      border: 1px solid rgba(0, 245, 212, 0.4);
      padding: 4px;
      font-family: monospace;
      font-size: 11px;
      z-index: 1000;
      box-shadow: 2px 2px 8px rgba(0,0,0,0.5);
    `;

    const tilePx = CELL_SIZE * camera.zoom;

    // ── Context menu for active selection ──────────────────────────────────
    if (selectedBldgs.size > 0) {
      const title = document.createElement('div');
      title.style.cssText = 'color: var(--cyan); padding: 2px 6px; border-bottom: 1px solid rgba(var(--ac),0.2); margin-bottom: 4px; font-weight: bold;';
      title.textContent = t('map.selection_count', { count: String(selectedBldgs.size) });
      contextMenuEl.appendChild(title);

      // Ensure plan mode is open for route actions
      if (!isPlanModeActive()) {
        const openPlanBtn = document.createElement('div');
        openPlanBtn.style.cssText = 'padding: 4px 6px; color: var(--cyan); cursor: pointer; transition: background 0.1s; font-weight: bold;';
        openPlanBtn.textContent = t('map.btn_plan') + ' aktivieren';
        openPlanBtn.addEventListener('mouseenter', () => openPlanBtn.style.background = 'rgba(0, 245, 212, 0.15)');
        openPlanBtn.addEventListener('mouseleave', () => openPlanBtn.style.background = 'transparent');
        openPlanBtn.addEventListener('click', () => {
          openPlanMode(root);
          draw();
          if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
        });
        contextMenuEl?.appendChild(openPlanBtn);
      }

      // Plan action items for selected building (plan mode)
      if (isPlanModeActive() && selectedBldgs.size > 0) {
        const firstNode = [...selectedBldgs][0];
        const sectorId = sortedBuildings?.find(b => b.nodeId === firstNode)?.sectorId ?? '';
        const allActions = fixture.player.actions ?? [];
        const planItems = buildPlanContextItems(sectorId, allActions, {
          buildingNodeId: firstNode,
          isOwned: fixture.player.buildings.some(b => b.nodeId === firstNode && b.owned),
        });
        for (const item of planItems) {
          item.addEventListener('click', () => {
            if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
            draw();
          });
          contextMenuEl?.appendChild(item);
        }
      } else if (selectedBldgs.size > 0) {
        const planBtn = document.createElement('div');
        planBtn.style.cssText = 'padding: 4px 6px; color: #e0e8f0; cursor: pointer; transition: background 0.1s;';
        planBtn.textContent = t('planui.add_to_plan');
        planBtn.addEventListener('mouseenter', () => planBtn.style.background = 'rgba(0, 245, 212, 0.15)');
        planBtn.addEventListener('mouseleave', () => planBtn.style.background = 'transparent');
        planBtn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          openPlanMode(root);
          draw();
          if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
        });
        contextMenuEl?.appendChild(planBtn);
      }

      const clearBtn = document.createElement('div');
      clearBtn.style.cssText = 'padding: 4px 6px; color: #e0e8f0; cursor: pointer; transition: background 0.1s;';
      clearBtn.textContent = t('map.clear_selection');
      clearBtn.addEventListener('mouseenter', () => clearBtn.style.background = 'rgba(231,57,70,0.15)');
      clearBtn.addEventListener('mouseleave', () => clearBtn.style.background = 'transparent');
      clearBtn.addEventListener('click', () => {
        clearSelection();
        if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
      });
      contextMenuEl?.appendChild(clearBtn);
    } else {
      // ── Standard sector context menu ──────────────────────────────────────
      // Building-level right-click: show building header
      const bldg = tilePx >= 12 ? buildingAt(w.x, w.y) : null;
      const bldgInfo = bldg ? worldBldgs?.get(bldg.nodeId) : null;

      const title = document.createElement('div');
      title.style.cssText = 'color: var(--cyan); padding: 2px 6px; border-bottom: 1px solid rgba(var(--ac),0.2); margin-bottom: 4px; font-weight: bold;';
      title.textContent = bldgInfo
        ? `${bldgInfo.display_name} — ${bldgInfo.category}`
        : t('map.ctx_title', { sector: s.sector_id });
      contextMenuEl.appendChild(title);

      // Determine ownership context for action filtering
      const playerBldgs = fixture.player?.buildings ?? [];
      const bldgOwned = bldg ? playerBldgs.some((pb: { nodeId?: string }) => pb.nodeId === bldg.nodeId) : false;
      const ownershipTag = bldgOwned ? 'own' : 'neutral';

      // Check if sector/building has recruitable tag
      // Context actions — driven by fixture.player.actions, filtered by target type + ownership
      const srvActions = fixture.player?.actions ?? [];
      const actions = srvActions
        .filter(a => {
          const tt = a.targetType ?? [];
          if (!tt.includes('building') && !tt.includes('node')) return false;
          // Filter by ownership when action specifies it
          const ow = a.targetOwnership;
          if (ow && ow.length > 0 && !ow.includes(ownershipTag)) return false;
          return true;
        });

      // Sweep option (zoomed out — no individual buildings visible)
      if (tilePx < 12 && sortedBuildings) {
        const sweepTargets = sortedBuildings.filter(
          b => b.sectorId === s.sector_id && isBuildingSelectable(b.nodeId)
        );
        if (sweepTargets.length > 0 && isPlanModeActive()) {
          const firstNode = sweepTargets[0].nodeId;
          const allActions = fixture.player.actions ?? [];
          const planItems = buildPlanContextItems(s.sector_id, allActions, {
            buildingNodeId: firstNode,
            isOwned: fixture.player.buildings.some(b => b.nodeId === firstNode && b.owned),
          });
          for (const item of planItems) {
            item.addEventListener('click', () => {
              if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
              draw();
            });
            contextMenuEl?.appendChild(item);
          }
        } else if (sweepTargets.length > 0) {
          const sweepBtn = document.createElement('div');
          sweepBtn.style.cssText = 'padding: 4px 6px; color: #fca311; cursor: pointer; transition: background 0.1s;';
          sweepBtn.textContent = t('map.sweep_count', { count: String(sweepTargets.length) });
          sweepBtn.addEventListener('mouseenter', () => sweepBtn.style.background = 'rgba(252,163,17,0.15)');
          sweepBtn.addEventListener('mouseleave', () => sweepBtn.style.background = 'transparent');
          sweepBtn.addEventListener('click', () => {
            openPlanMode(root);
            draw();
            if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
          });
          contextMenuEl?.appendChild(sweepBtn);
        }
      }

      // Filter-aware quick actions (show only when building-level + filter suggests an action)
      if (bldgInfo && isAnyFilterActive()) {
        const nodeFilter = getNodeFilter(bldgInfo.node_id);
        if (nodeFilter) {
          const planAction = getFilterPlanAction(nodeFilter);
          const filterDef = FILTERS.find(f => f.id === nodeFilter);
          if (planAction && filterDef) {
            const fBtn = document.createElement('div');
            fBtn.style.cssText = `padding: 4px 6px; color: ${filterDef.color}; cursor: pointer; transition: background 0.1s; font-weight: bold;`;
            fBtn.textContent = t('map.filter_plan', { label: filterDef.label });
            fBtn.addEventListener('mouseenter', () => fBtn.style.background = `${filterDef.color}18`);
            fBtn.addEventListener('mouseleave', () => fBtn.style.background = 'transparent');
            fBtn.addEventListener('click', () => {
              if (!isPlanModeActive()) openPlanMode(root);
              draw();
              if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
            });
            contextMenuEl?.appendChild(fBtn);
          }
        }
      }

      // Plan mode: show runner-specific action items
      if (isPlanModeActive()) {
        const planItems = buildPlanContextItems(s.sector_id, actions, {
          buildingNodeId: bldg?.nodeId,
          isOwned: bldgOwned,
        });
        for (const item of planItems) {
          item.addEventListener('click', () => {
            if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
            draw();
          });
          contextMenuEl?.appendChild(item);
        }
      } else {
        // Non-plan-mode: show action labels that open plan mode
        actions.forEach(act => {
          const btn = document.createElement('div');
          btn.style.cssText = 'padding: 4px 6px; color: #e0e8f0; cursor: pointer; transition: background 0.1s;';
          btn.textContent = act.label;
          btn.addEventListener('mouseenter', () => btn.style.background = 'rgba(0, 245, 212, 0.15)');
          btn.addEventListener('mouseleave', () => btn.style.background = 'transparent');
          btn.addEventListener('click', () => {
            openPlanMode(root);
            draw();
            if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
          });
          contextMenuEl?.appendChild(btn);
        });
      }
    }

    if (contextMenuEl) {
      document.body.appendChild(contextMenuEl);
    }
  });

  // Close context menu on any other click (auto-cleanup when screen unmounts)
  function onDocClick(e: MouseEvent): void {
    if (!document.contains(root)) {
      document.removeEventListener('click', onDocClick);
      // Cleanup body-level elements when map screen unmounts
      removeFab();
      hideBuildingTooltip();
      unmountRunnerBar();
      closePlanMode();
      if (contextMenuEl) { contextMenuEl.remove(); contextMenuEl = null; }
      return;
    }
    if (contextMenuEl && e.button !== 2) {
      contextMenuEl.remove();
      contextMenuEl = null;
    }
  }
  document.addEventListener('click', onDocClick);

  function clampPan() {
    // World is WORLD_W wide; visible viewport = CW/camera.zoom in world coords.
    // At camera.x=0 left edge of world aligns with left edge of canvas.
    const maxPanX = WORLD_W * camera.zoom - CW;
    const maxPanY = WORLD_H * camera.zoom - CH;
    camera.x = Math.min(0, Math.max(-maxPanX, camera.x));
    camera.y = Math.min(0, Math.max(-maxPanY, camera.y));
  }

  // ── Panel ─────────────────────────────────────────────────────────────────

  function showSectorPanel(id: string) {
    const s = sectorMap.get(id);
    if (!s) return;
    const live     = fixtureSectors.get(id);
    const scouted  = isScouted(id);

    // ── Unscouted sector: minimal info only ──────────────────────────
    if (!scouted) {
      detailPanel.innerHTML = `
        <div class="dp-id">${s.sector_id} · ${s.sector_type.replace(/_/g,' ')}</div>
        <div class="dp-title">${s.name}</div>
        <div class="dp-type">${s.sector_type.replace(/_/g,' ')}</div>
        <div style="margin:16px 0;padding:12px;background:rgba(4,6,12,0.6);border:1px dashed rgba(var(--ac),0.15);border-radius:4px;text-align:center">
          <div style="font-size:18px;margin-bottom:6px;opacity:0.4">🔒</div>
          <div style="font-size:12px;color:#4a5a6a">Sektor nicht aufgeklärt</div>
          <div style="font-size:11px;color:#3a4a5a;margin-top:4px">RECON-Aktion erforderlich</div>
        </div>
        <div class="dp-section">${t('map.section_neighbors')}</div>
        <div style="margin-top:4px">
          ${(s.adjacent_sectors ?? []).map(adjId => {
            const adj = sectorMap.get(adjId);
            return '<span class="adj-tag chip" data-goto="' + adjId + '">' + adjId + ' <span style="color:#3a4a5a;font-size:11px">' + (adj?.name.split(' ')[0] ?? '') + '</span></span>';
          }).join('')}
        </div>
      `;
      detailPanel.querySelectorAll<HTMLElement>('.adj-tag[data-goto]').forEach(btn => {
        btn.addEventListener('click', () => {
          selectedId = btn.dataset.goto!;
          showSectorPanel(btn.dataset.goto!);
          draw();
        });
      });
      return;
    }

    // ── Scouted sector: full details ─────────────────────────────────
    const liveHeat = live?.heat ?? s.heat;
    const liveOwner = live?.owner ?? null;
    const aiName   = aiSectorName.get(id);

    const nodeCounts: Record<string, number> = {};
    s.nodes.forEach(n => { nodeCounts[n.node_type] = (nodeCounts[n.node_type] ?? 0) + 1; });

    const playerBuildings = (fixture.player?.buildings ?? []).filter(
      (b: { sector?: string }) => b.sector === id
    );

    let ownerBadge = '';
    if (liveOwner) {
      const c = getOwnerColor(liveOwner, myCorpName);
      ownerBadge = `<div style="margin-bottom:10px;padding:5px 10px;background:${c}18;border:1px solid ${c}44;border-radius:3px;font-size:12px;color:${c}">${liveOwner === myCorpName ? t('map.sector_your_territory') : t('map.sector_occupied', { owner: liveOwner })}</div>`;
    } else if (aiName) {
      ownerBadge = `<div style="margin-bottom:10px;padding:5px 10px;background:rgba(122,58,170,0.12);border:1px solid rgba(122,58,170,0.3);border-radius:3px;font-size:11px;color:#9b59b6">${t('map.sector_ai', { name: aiName })}</div>`;
    }

    const nodeTypeLabels: Record<string, string> = { BUILDING_ENTRY:t('map.node_buildings'), STREET:t('map.node_streets'), ALLEY:t('map.node_alleys'), BORDER:t('map.node_borders') };
    const nodeTypeDotColors: Record<string, string> = { BUILDING_ENTRY:'var(--cyan)', STREET:'rgba(130,160,200,0.7)', ALLEY:'rgba(90,110,140,0.5)', BORDER:'rgba(140,160,180,0.5)' };
    const nodeTypeRows = (['BUILDING_ENTRY','STREET','ALLEY','BORDER'] as const)
      .filter(t => nodeCounts[t])
      .map(t => `<div style="display:flex;align-items:center;gap:8px;padding:3px 0;font-size:12px;color:#6a7a8a">
        <div style="width:8px;height:8px;border-radius:50%;background:${nodeTypeDotColors[t]};flex-shrink:0"></div>
        ${nodeTypeLabels[t]}<span style="font-family:monospace;margin-left:auto;color:#4a5a6a">${nodeCounts[t]}</span>
      </div>`).join('');

    const buildingSection = playerBuildings.length > 0
      ? `<div class="dp-section">${t('map.section_your_buildings')}</div>
         ${playerBuildings.map((b: { name: string; type?: string; isFront?: boolean; income?: number; heat?: number }) => `
           <div class="bld-card">
             <div class="bld-name">${b.name}</div>
             <div class="bld-meta">${b.type ?? ''}${b.isFront ? ' · FRONT' : ''} · +$${(b.income ?? 0).toLocaleString('de-DE')}/Zyklus</div>
           </div>`).join('')}`
      : '';

    // Filter summary for this sector
    let filterSummaryHtml = '';
    if (isAnyFilterActive() && sortedBuildings) {
      const counts = new Map<FilterId, number>();
      for (const b of sortedBuildings) {
        if (b.sectorId !== id) continue;
        const fid = getNodeFilter(b.nodeId);
        if (fid) counts.set(fid, (counts.get(fid) ?? 0) + 1);
      }
      if (counts.size > 0) {
        const parts = [...counts.entries()].map(([fid, n]) => {
          const f = FILTERS.find(x => x.id === fid);
          return `<span style="color:${f?.color ?? '#fff'}">${n} ${f?.label ?? fid}</span>`;
        });
        filterSummaryHtml = `<div style="margin:6px 0;padding:5px 8px;background:rgba(var(--ac),0.04);border:1px solid rgba(var(--ac),0.1);border-radius:3px;font-size:11px;color:#8a9aaa">${parts.join(' · ')}</div>`;
      }
    }

    detailPanel.innerHTML = `
      <div class="dp-id">${s.sector_id} · ${s.sector_type.replace(/_/g,' ')}</div>
      <div class="dp-title">${s.name}</div>
      <div class="dp-type">${s.sector_type.replace(/_/g,' ')}</div>
      ${ownerBadge}
      ${filterSummaryHtml}
      <div class="dp-section">${t('map.section_live')}</div>
      <div class="dp-row"><span class="dp-label">Heat</span><span class="dp-val" style="color:${heatColor(liveHeat)}">${liveHeat.toFixed(0)}</span></div>
      ${showHeatPrediction && sectorHeatDeltas.has(id) ? (() => {
        const delta = sectorHeatDeltas.get(id)!;
        const predicted = liveHeat + delta;
        return `<div class="dp-row"><span class="dp-label" style="color:#fca311">${t('map.section_planned')}</span><span class="dp-val" style="color:#fca311">+${delta} → ${predicted.toFixed(0)}</span></div>`;
      })() : ''}
      <div class="dp-section">Statistiken</div>
      <div class="dp-row"><span class="dp-label">${t('map.stat_prosperity')}</span><span class="dp-val">${s.wealth}</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.stat_fear')}</span><span class="dp-val">${s.fear}</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.stat_addiction')}</span><span class="dp-val">${s.addiction}</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.stat_property_value')}</span><span class="dp-val">$${s.property_value.toLocaleString('de-DE')}</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.stat_laundering_cap')}</span><span class="dp-val">$${s.laundering_cap.toLocaleString('de-DE')}</span></div>
      <div class="dp-section">${t('map.section_infra', { count: String(s.nodes.length) })}</div>
      ${nodeTypeRows}
      ${buildingSection}
      <div class="dp-section">${t('map.section_neighbors')}</div>
      <div style="margin-top:4px">
        ${s.adjacent_sectors.map(adjId => {
          const adj = sectorMap.get(adjId);
          return `<span class="adj-tag chip" data-goto="${adjId}">${adjId} <span style="color:#3a4a5a;font-size:11px">${adj?.name.split(' ')[0] ?? ''}</span></span>`;
        }).join('')}
      </div>
    `;

    detailPanel.querySelectorAll<HTMLElement>('.adj-tag[data-goto]').forEach(btn => {
      btn.addEventListener('click', () => {
        selectedId = btn.dataset.goto!;
        showSectorPanel(btn.dataset.goto!);
        draw();
      });
    });
  }

  // ── Building Detail Panel ─────────────────────────────────────────────────

  function showBuildingPanel(b: SortedBuilding) {
    const bldg = worldBldgs?.get(b.nodeId);
    if (!bldg) {
      // Fallback: show sector panel for this building's sector
      selectedId = b.sectorId;
      showSectorPanel(b.sectorId);
      return;
    }

    const playerBldgs = fixture.player?.buildings ?? [];
    const isOwned = playerBldgs.some((pb: { nodeId?: string }) => pb.nodeId === b.nodeId);
    const accent = getAccentColor();
    const sectorName = sectorMap.get(b.sectorId)?.name ?? b.sectorId;

    // Status badge
    let statusBadge: string;
    if (isOwned) {
      statusBadge = `<div style="margin:8px 0;padding:5px 10px;background:${accent}18;border:1px solid ${accent}44;border-radius:3px;font-size:12px;color:${accent}">${t('map.bldg_owned')}</div>`;
    } else if (bldg.illegal) {
      statusBadge = `<div style="margin:8px 0;padding:5px 10px;background:rgba(231,76,60,0.12);border:1px solid rgba(231,76,60,0.3);border-radius:3px;font-size:11px;color:#e74c3c">${t('map.bldg_illegal')}</div>`;
    } else {
      statusBadge = `<div style="margin:8px 0;padding:5px 10px;background:rgba(58,74,90,0.2);border:1px solid rgba(58,74,90,0.3);border-radius:3px;font-size:11px;color:#6a7a8a">${t('map.bldg_available')}</div>`;
    }

    // HQ slot badge
    const hqBadge = bldg.is_hq_slot
      ? `<div style="margin-bottom:6px;padding:3px 8px;background:rgba(252,163,17,0.12);border:1px solid rgba(252,163,17,0.3);border-radius:3px;font-size:12px;color:#fca311">${t('map.bldg_hq_slot')}</div>`
      : '';

    detailPanel.innerHTML = `
      <div class="dp-id">${b.nodeId} · ${bldg.category}</div>
      <div class="dp-title">${bldg.display_name}</div>
      <div class="dp-type">${bldg.zone ?? ''} · ${sectorName}</div>
      ${statusBadge}
      ${hqBadge}
      <div class="dp-section">${t('map.bldg_section_economy')}</div>
      <div class="dp-row"><span class="dp-label">${t('map.bldg_rent')}</span><span class="dp-val">$${bldg.rent.toLocaleString('de-DE')}/Zyklus</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.bldg_buy_price')}</span><span class="dp-val">$${bldg.buy_price.toLocaleString('de-DE')}</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.bldg_income')}</span><span class="dp-val">$${bldg.income_base.toLocaleString('de-DE')}/Zyklus</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.bldg_defense')}</span><span class="dp-val">${bldg.defense_value ?? 0}</span></div>
      ${bldg.storage_cap ? `<div class="dp-row"><span class="dp-label">${t('map.bldg_storage')}</span><span class="dp-val">${bldg.storage_cap}</span></div>` : ''}
      ${bldg.extort_rate?.[1] ? `<div class="dp-row"><span class="dp-label">${t('map.bldg_extort')}</span><span class="dp-val">$${bldg.extort_rate[0]}–${bldg.extort_rate[1]}</span></div>` : ''}
      <div class="dp-section">${t('map.bldg_section_details')}</div>
      <div class="dp-row"><span class="dp-label">${t('map.bldg_category')}</span><span class="dp-val">${bldg.category}</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.bldg_illegal_label')}</span><span class="dp-val">${bldg.illegal ? t('map.bldg_yes') : t('map.bldg_no')}</span></div>
      <div class="dp-row"><span class="dp-label">${t('map.bldg_front_company')}</span><span class="dp-val">${bldg.front_company ? t('map.bldg_yes') : t('map.bldg_no')}</span></div>
      ${bldg.synergy_tags?.length ? `<div class="dp-row"><span class="dp-label">${t('map.bldg_synergies')}</span><span class="dp-val">${bldg.synergy_tags.join(', ')}</span></div>` : ''}
      <div class="dp-section">${t('map.bldg_section_actions')}</div>
      <div id="bldg-actions" style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px"></div>
      <div style="margin-top:14px;padding-top:8px;border-top:1px solid rgba(var(--ac),0.07)">
        <span class="adj-tag chip" data-goto-sector="${b.sectorId}">← ${sectorName}</span>
      </div>
    `;

    // Action buttons — driven by fixture.player.actions
    const actionsEl = detailPanel.querySelector('#bldg-actions')!;
    const ownershipTag = isOwned ? 'own' : 'neutral'; // TODO: detect 'rival' when building owner info is available
    const serverActions = fixture.player?.actions ?? [];
    const buildingActions = serverActions.filter(a => {
      if (!(a.targetType ?? []).includes('building')) return false;
      const ow = a.targetOwnership;
      if (ow && ow.length > 0 && !ow.includes(ownershipTag)) return false;
      return true;
    });

    for (const act of buildingActions) {
      const btn = document.createElement('button');
      btn.className = 'adj-tag chip';
      btn.textContent = `⚡ ${act.label}`;
      btn.style.cursor = 'pointer';
      btn.addEventListener('click', () => {
        if (!isPlanModeActive()) openPlanMode(root);
        // In plan mode, user still needs to select a runner — the button opens plan mode at the right sector
        draw();
      });
      actionsEl.appendChild(btn);
    }

    // Back to sector link
    detailPanel.querySelector<HTMLElement>('[data-goto-sector]')?.addEventListener('click', () => {
      selectedId = b.sectorId;
      showSectorPanel(b.sectorId);
      draw();
    });
  }

  // ── Toolbar wiring ────────────────────────────────────────────────────────

  root.querySelectorAll<HTMLElement>('.overlay-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll<HTMLElement>('.overlay-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentOverlay = btn.dataset.overlay as OverlayMode;
      updateLegend(currentOverlay, CELL_SIZE * camera.zoom >= 12);
      draw();
    });
  });

  const heatPredictToggle = root.querySelector<HTMLElement>('#heat-predict-toggle');
  if (heatPredictToggle) {
    heatPredictToggle.addEventListener('click', () => {
      showHeatPrediction = !showHeatPrediction;
      heatPredictToggle.classList.toggle('active', showHeatPrediction);
      draw();
    });
  }

  const filterToggle = root.querySelector<HTMLElement>('#filter-toggle');
  if (filterToggle) {
    filterToggle.addEventListener('click', () => toggleFilterSidebar());
  }

  const planToggle = root.querySelector<HTMLElement>('#plan-toggle');
  if (planToggle) {
    planToggle.addEventListener('click', () => {
      togglePlanMode(root);
      planToggle.classList.toggle('active', isPlanModeActive());
      draw();
    });
  }

  // ── Runner bar — always visible at the bottom of the map ────────────────
  mountRunnerBar(root);

  // ── Data loading ──────────────────────────────────────────────────────────

  // Show loading state
  ctx.fillStyle = '#0a0c14';
  ctx.fillRect(0, 0, CW, CH);
  ctx.fillStyle = accent(0.3);
  ctx.font = '12px JetBrains Mono, monospace';
  ctx.textAlign = 'center';
  ctx.fillText('Lade Kartendaten…', CW / 2, CH / 2);

  // Load per-session map data from WS (set by map_data event).
  // Poll briefly if not yet available (WS map_data may arrive after state_update).
  function waitForMapData(attempts: number): Promise<NonNullable<typeof mapData>> {
    if (mapData) return Promise.resolve(mapData);
    if (attempts <= 0) return Promise.reject(new Error('Keine Kartendaten vom Server erhalten'));
    return new Promise(r => setTimeout(r, 250)).then(() => waitForMapData(attempts - 1));
  }

  waitForMapData(20)
    .then(md => {
      const worldBldgsPromise: Promise<Map<string, WorldBuilding>> =
        Promise.resolve(new Map((md.worldBuildings ?? []).map((b: any) => [b.node_id, b as WorldBuilding])));

      return Promise.resolve(md.cityGraph as CityGraph)
        .then((data: CityGraph) => {
      graph = data;

      // Initialize world dimensions for square cells
      initWorldDimensions(data.meta.grid_cols, data.meta.grid_rows);
      // Re-set camera zoom to fit the corrected world
      camera.zoom = INIT_ZOOM;

      // Build node lookup + footprint→entry mapping
      for (const s of data.sectors) {
        sectorMap.set(s.sector_id, s);
        for (const n of s.nodes) {
          nodeMap.set(n.node_id, n);
          if (n.node_type === 'BUILDING_FOOTPRINT' && n.footprint_of) {
            footprintToEntry.set(n.node_id, n.footprint_of);
          }
        }
      }

      // Precompute sector bounding boxes from node world coords
      for (const s of data.sectors) {
        if (s.nodes.length === 0) continue;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const n of s.nodes) {
          const x = wx(n.global_x), y = wy(n.global_y);
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
        sectorBounds.set(s.sector_id, { x: minX, y: minY, w: maxX - minX, h: maxY - minY });
      }

      // AI corp sectors
      for (const corp of data.meta.ai_corps.corps) {
        for (const sid of corp.sectors) {
          aiSectors.add(sid);
          aiSectorName.set(sid, corp.name);
        }
      }

      // Update version badge
      const badge = root.querySelector<HTMLElement>('#map-version-badge');
      if (badge) {
        const m = data.meta;
        badge.textContent = [
          m.version ? `v${m.version}` : '',
          m.engine_version ?? '',
          m.generated ?? '',
        ].filter(Boolean).join(' · ');
      }

      // Compute road bitmasks (must happen before first sprite draw)
      roadBitmasks = buildRoadBitmasks(data.sectors);
      crossConnectors = buildCrossConnectors(data.sectors);

      draw();

      // Load buildings + sprites in parallel, then build Y-sorted array once
      Promise.all([
        worldBldgsPromise,
        preloadSprites(),
      ]).then(([bldgs]) => {
        worldBldgs = bldgs;

        // Initial filter recompute if filters were active before data loaded
        if (isAnyFilterActive()) recomputeFilters();

        // ── Build city blocks + sorted building list ──────────────────────
        const gridRes = 15;
        const arr: SortedBuilding[] = [];
        const blocks: CityBlock[] = [];
        let blockCounter = 0;

        for (const s of data.sectors) {
          // Build grid lookup: (row, col) → { node_type, node_id, global_x, global_y }
          type GridCell = { type: string; nodeId: string; gx: number; gy: number };
          const sectorGrid: (GridCell | null)[][] = Array.from({ length: gridRes }, () => Array(gridRes).fill(null));
          for (const n of s.nodes) {
            const col = Math.round(n.global_x * data.meta.grid_cols * (gridRes + 1)) - s.grid_col * (gridRes + 1) - 1;
            const row = Math.round(n.global_y * data.meta.grid_rows * (gridRes + 1)) - s.grid_row * (gridRes + 1) - 1;
            if (col >= 0 && col < gridRes && row >= 0 && row < gridRes) {
              sectorGrid[row][col] = { type: n.node_type, nodeId: n.node_id, gx: n.global_x, gy: n.global_y };
            }
          }

          // Flood-fill to find blocks: BUILDING_ENTRY + ALLEY cells bounded by STREET/AVENUE
          const visited = Array.from({ length: gridRes }, () => Array(gridRes).fill(false));
          const isBlockCell = (r: number, c: number) => {
            if (r < 0 || r >= gridRes || c < 0 || c >= gridRes) return false;
            const cell = sectorGrid[r][c];
            return cell?.type === 'BUILDING_ENTRY' || cell?.type === 'BUILDING_FOOTPRINT' || cell?.type === 'ALLEY' || cell?.type === 'COURTYARD';
          };

          for (let row = 0; row < gridRes; row++) {
            for (let col = 0; col < gridRes; col++) {
              if (visited[row][col] || !isBlockCell(row, col)) continue;

              // Flood-fill this block
              const blockId = blockCounter++;
              const cells: CityBlock['cells'] = [];
              const buildingNodeIds: string[] = [];
              const stack: [number, number][] = [[row, col]];
              while (stack.length > 0) {
                const [r, c] = stack.pop()!;
                if (r < 0 || r >= gridRes || c < 0 || c >= gridRes) continue;
                if (visited[r][c] || !isBlockCell(r, c)) continue;
                visited[r][c] = true;
                const cell = sectorGrid[r][c]!;
                cells.push({ row: r, col: c, type: cell.type, wx: wx(cell.gx), wy: wy(cell.gy) });
                if (cell.type === 'BUILDING_ENTRY') buildingNodeIds.push(cell.nodeId);
                stack.push([r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]);
              }

              if (cells.length === 0) continue;

              // Compute bounding box (half cell padding to fill to edges)
              const halfCell = CELL_SIZE / 2;
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              for (const cell of cells) {
                if (cell.wx - halfCell < minX) minX = cell.wx - halfCell;
                if (cell.wy - halfCell < minY) minY = cell.wy - halfCell;
                if (cell.wx + halfCell > maxX) maxX = cell.wx + halfCell;
                if (cell.wy + halfCell > maxY) maxY = cell.wy + halfCell;
              }

              // Average tint from building categories
              const bldgCells = cells.filter(c => c.type === 'BUILDING_ENTRY');
              const tints = bldgCells.map(c => {
                const bldg = bldgs.get(sectorGrid[c.row][c.col]?.nodeId ?? '');
                return getCategoryTint(bldg?.category ?? '');
              });
              const tint = tints[0] ?? '#1a2438';

              blocks.push({
                id: blockId,
                sectorId: s.sector_id,
                sectorCol: s.grid_col,
                sectorRow: s.grid_row,
                minX, minY, maxX, maxY,
                cells,
                facadeN: [], facadeE: [], facadeS: [], facadeW: [],
                buildings: buildingNodeIds,
                tint,
              });

              // Add individual buildings to sortedBuildings for detail rendering
              for (const n of s.nodes) {
                if (n.node_type !== 'BUILDING_ENTRY') continue;
                if (!buildingNodeIds.includes(n.node_id)) continue;
                const bldg = bldgs.get(n.node_id);
                const cat = bldg?.category ?? '';
                const seed = hashStr(bldg?.instance_id ?? n.node_id);
                const tiles = CATEGORY_TILES[cat] ?? DEFAULT_TILES;
                const tileIdx = tiles[seed % tiles.length];
                arr.push({
                  wx: wx(n.global_x), wy: wy(n.global_y),
                  cat, tint: getCategoryTint(cat),
                  sectorCol: s.grid_col, sectorRow: s.grid_row,
                  tileIdx, nodeId: n.node_id, sectorId: s.sector_id,
                  facing: n.facing ?? 'S', blockId,
                });
              }

              // Add BUILDING_FOOTPRINT cells — tint overlay + click target, no sprite
              for (const cell of cells) {
                if (cell.type !== 'BUILDING_FOOTPRINT') continue;
                const gridCell = sectorGrid[cell.row][cell.col];
                if (!gridCell) continue;
                const entryId = footprintToEntry.get(gridCell.nodeId);
                if (!entryId) continue;
                const entryBldg = bldgs.get(entryId);
                const cat = entryBldg?.category ?? '';
                arr.push({
                  wx: cell.wx, wy: cell.wy,
                  cat, tint: getCategoryTint(cat),
                  sectorCol: s.grid_col, sectorRow: s.grid_row,
                  tileIdx: 0, nodeId: gridCell.nodeId, sectorId: s.sector_id,
                  facing: '', blockId,
                  isFootprint: true, entryNodeId: entryId,
                });
              }
            }
          }
        }
        arr.sort((a, b) => a.wy - b.wy);   // ascending Y → back-to-front
        sortedBuildings = arr;
        cityBlocks = blocks;

        draw();
      });
    });
    }) // end waitForMapData().then(md => graphPromise.then(...))
    .catch((err) => {
      ctx.clearRect(0, 0, CW, CH);
      ctx.fillStyle = '#f72585';
      ctx.font = '12px JetBrains Mono, monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`${err}`, CW / 2, CH / 2);
    });

  return root;
}
